/**
 * Prestige Model Service - One-to-one port from Vue app
 * Handles model loading, dimension changes, louvers, and accessories
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { RAL_COLOR_MAP } from '@domain/constants/ralColors';
import { PRESTIGE_DIMENSIONS } from '@domain/constants/prestigeDimensions';
import { getPrestigeExternalWidthCm } from '@domain/services/prestigeDimensionDisplay';
import { resolvePublicAssetUrl } from './assetPath';

const getAccessoryImageSrc = ({ fileName }: { fileName: string }): string => {
  return resolvePublicAssetUrl({ relativePath: `images/${fileName}` });
};

// 13'9" = 419.1 cm; matches the factory configurator threshold of 420 cm.
const SLIDING_ADJUSTABLE_SHUTTER_TRACK_THRESHOLD_CM = 420;

type SlidingAdjustableShutterTrackCount = 2 | 3;

export const getSlidingAdjustableShutterTrackCount = ({
  widthValue,
}: {
  widthValue: number;
}): SlidingAdjustableShutterTrackCount => {
  return getPrestigeExternalWidthCm(widthValue) < SLIDING_ADJUSTABLE_SHUTTER_TRACK_THRESHOLD_CM ? 2 : 3;
};

const getSlidingAdjustableShutterTrackCountForSpan = ({
  spanMeters,
}: {
  spanMeters: number;
}): SlidingAdjustableShutterTrackCount => {
  return spanMeters * 100 < SLIDING_ADJUSTABLE_SHUTTER_TRACK_THRESHOLD_CM ? 2 : 3;
};

const getSlidingAdjustableShutterModelPath = ({
  trackCount,
}: {
  trackCount: SlidingAdjustableShutterTrackCount;
}): string => {
  return trackCount === 2
    ? 'slidingAdjustableShutter_2tracks.glb'
    : 'slidingAdjustableShutter_3tracks.glb';
};

const getSlidingAdjustableShutterImageFileName = ({
  size,
  widthValue,
}: {
  size: 'big' | 'small';
  widthValue: number;
}): string => {
  const trackCount = getSlidingAdjustableShutterTrackCount({ widthValue });
  const suffix = size === 'big' ? 'BIG' : 'SMALL';
  return `SLIDING_ADJUSTABLE_SHUTTER_PANEL_${trackCount}TRACK_${suffix}.jpg`;
};

// Face IDs matching Vue app (includes dual-room faces)
export const FaceID = {
  East: 'East',
  West: 'West',
  South0: 'South0',  // Room 1 south face
  South1: 'South1',  // Room 2 south face
  North0: 'North0',  // Room 1 north face
  North1: 'North1',  // Room 2 north face
} as const;

export type FaceIDType = typeof FaceID[keyof typeof FaceID];

// Part types matching Vue app
export const PartType = {
  NONE: 0,
  MANUAL_WIND_SCREEN: 1,
  MOTORIZED_WIND_SCREEN: 2,
  HORIZONTAL_SCREEN_PANEL: 3,
  VERTICAL_SCREEN_PANEL: 4,
  DECO_SCREEN: 5,
  TILTING_HORIZONTAL_SCREEN_PANEL: 6,
  WPC_FENCING_SCREEN: 7,
  FRAMELESS_GLASS_DOOR: 8,
  SLIMLINE_FRAME_GLASS_DOOR: 9,
  SLIDING_DOOR_FABRIC: 10,
  WPC_WALL: 12,
  VERTICAL_WPC_DECORATION_SLAT: 13,
  DECO_YUGUWEN: 15,
  DECO_A: 16,
  SLIDING_ADJUSTABLE_SHUTTER_PANEL: 17,
} as const;

export type PartTypeValue = typeof PartType[keyof typeof PartType];

// Door types for assembly
export const DoorType = {
  SimpleOne: 1,  // Single unit (curtains, glass doors)
  Merge6: 2,     // 3-6 panels combined (screen panels)
} as const;

// Accessory model paths (flat structure - no subdirectories)
export const ACCESSORY_MODELS: Record<number, string> = {
  [PartType.MANUAL_WIND_SCREEN]: 'curtain.glb',
  [PartType.MOTORIZED_WIND_SCREEN]: 'curtain.glb',
  [PartType.HORIZONTAL_SCREEN_PANEL]: 'horizontalGrid.glb',
  [PartType.VERTICAL_SCREEN_PANEL]: 'verticalGrid.glb',
  [PartType.DECO_SCREEN]: 'decoGrid.glb',
  [PartType.TILTING_HORIZONTAL_SCREEN_PANEL]: 'tiltingHorizontalGrid.glb',
  [PartType.WPC_FENCING_SCREEN]: 'wpcFencingGrid.glb',
  [PartType.FRAMELESS_GLASS_DOOR]: 'slidingDoor4.glb',
  [PartType.SLIMLINE_FRAME_GLASS_DOOR]: 'slidingDoor2.glb',
  [PartType.SLIDING_DOOR_FABRIC]: 'slidingDoorFabric.glb',
  [PartType.WPC_WALL]: 'wpcWall.glb',
  [PartType.VERTICAL_WPC_DECORATION_SLAT]: 'verticalWpcDecoSlat.glb',
  [PartType.DECO_YUGUWEN]: 'decoYuguwen.glb',
  [PartType.DECO_A]: 'decoA.glb',
  [PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL]: 'slidingAdjustableShutter_2tracks.glb',
};

// Map part type to user-friendly name
export const PART_TYPE_NAMES: Record<number, string> = {
  [PartType.MANUAL_WIND_SCREEN]: 'Manual Wind Screen',
  [PartType.MOTORIZED_WIND_SCREEN]: 'Motorized Wind Screen',
  [PartType.HORIZONTAL_SCREEN_PANEL]: 'Horizontal Screen Panel',
  [PartType.VERTICAL_SCREEN_PANEL]: 'Vertical Screen Panel',
  [PartType.DECO_SCREEN]: 'Deco Screen',
  [PartType.TILTING_HORIZONTAL_SCREEN_PANEL]: 'Tilting Horizontal Screen Panel',
  [PartType.WPC_FENCING_SCREEN]: 'WPC Fencing Screen',
  [PartType.FRAMELESS_GLASS_DOOR]: 'Frameless Glass Door',
  [PartType.SLIMLINE_FRAME_GLASS_DOOR]: 'Sliding Glass Door',
  [PartType.SLIDING_DOOR_FABRIC]: 'Sliding Door Fabric',
  [PartType.WPC_WALL]: 'Solid Composite Privacy Wall',
  [PartType.VERTICAL_WPC_DECORATION_SLAT]: 'Vertical WPC Decoration Slat',
  [PartType.DECO_YUGUWEN]: 'Deco Fishbone Screen',
  [PartType.DECO_A]: 'Deco A Screen',
  [PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL]: 'Sliding Adjustable Shutter Panel',
};

export type PrestigeAccessoryCatalogItem = {
  partType: number;
  name: string;
  description: string;
  imgSrc: string;
};

export const getPrestigeAccessoryCatalog = ({
  widthValue,
}: {
  widthValue: number;
}): PrestigeAccessoryCatalogItem[] => [
  {
    partType: PartType.MOTORIZED_WIND_SCREEN,
    name: 'Motorized Wind Screen',
    description: 'Motorized Wind Screen offers remote-controlled adjustment for shade, wind protection, and privacy—allowing effortless control of your outdoor space.',
    imgSrc: getAccessoryImageSrc({ fileName: 'MOTORIZED_WIND_SCREEN.jpg' }),
  },
  {
    partType: PartType.SLIMLINE_FRAME_GLASS_DOOR,
    name: 'Sliding Glass Door',
    description: '8mm clear glass doors provide bright, open views, supported by a durable aluminum frame, dual-track system, and hydraulic soft-close for smooth, quiet operation.',
    imgSrc: getAccessoryImageSrc({ fileName: 'SLIMLINE_FRAME_GLASS_DOOR.jpg' }),
  },
  {
    partType: PartType.WPC_WALL,
    name: 'Solid Composite Privacy Wall',
    description: 'Fixed composite privacy wall made from durable WPC material, designed to provide permanent wind and sun protection with added privacy and a modern finish.',
    imgSrc: getAccessoryImageSrc({ fileName: 'WPC_WALL.jpg' }),
  },
  {
    partType: PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL,
    name: 'Sliding Adjustable Shutter Panel',
    description: 'Sliding adjustable shutter panel delivers smooth-glide operation with adjustable louvers, offering effortless control of light, airflow, and privacy',
    imgSrc: getAccessoryImageSrc({
      fileName: getSlidingAdjustableShutterImageFileName({ size: 'big', widthValue }),
    }),
  },
];

// Accessory catalog with Vue parity (names, descriptions, and thumbnail images)
export const PRESTIGE_ACCESSORY_CATALOG: PrestigeAccessoryCatalogItem[] = getPrestigeAccessoryCatalog({
  widthValue: PRESTIGE_DIMENSIONS.width[0] ?? 19,
});

// Prestige available accessory part types (derived from catalog)
export const PRESTIGE_ACCESSORIES = PRESTIGE_ACCESSORY_CATALOG.map(item => item.partType);

// Surface part tracking (face -> accessory mapping)
export type SurfacePart = {
  faceId: FaceIDType;
  visible: boolean;
  partType: number | null;
  part: THREE.Group | null;
};

export { PRESTIGE_DIMENSIONS };

const DEFAULT_DEPTH = PRESTIGE_DIMENSIONS.depth[0] ?? 3;
const DEFAULT_HEIGHT = PRESTIGE_DIMENSIONS.height[0] ?? 2.6;

// LED louver placement mapping (count -> positions)
const LED_POSITIONS: Record<number, number[][]> = {
  19: [[7, 13], [7, 13]],
  20: [[7, 14], [7, 14]],
  21: [[7, 15], [7, 15]],
  22: [[7, 15], [7, 15]],
  23: [[8, 16], [8, 16]],
  24: [[8, 17], [8, 17]],
  25: [[9, 17], [9, 17]],
  26: [[9, 18], [9, 18]],
  27: [[9, 19], [9, 19]],
  28: [[7, 14, 21], [9, 14, 21]],
  29: [[7, 15, 23], [9, 15, 23]],
  30: [[7, 16, 24], [9, 16, 24]],
  31: [[8, 16, 24], [8, 16, 24]],
  32: [[8, 16, 24], [8, 16, 24]],
  33: [[9, 18, 27], [9, 18, 27]],
  34: [[9, 18, 27], [9, 18, 27]],
  35: [[7, 14, 21, 28], [7, 14, 21, 28]],
  36: [[8, 16, 24, 30], [8, 16, 24, 30]],
  37: [[8, 16, 23, 30], [8, 16, 23, 30]],
  38: [[8, 16, 23, 31], [8, 16, 23, 31]],
};

export type PrestigeServiceState = {
  scene: THREE.Scene | null;
  group: THREE.Group;
  model: THREE.Group | null;

  // Dimension values
  widthValue: number;  // Louver count (19-76)
  depthValue: number;  // Actual depth (3-4)
  heightValue: number; // Actual height (2.6-3)

  // Model tracking
  modelSize: THREE.Vector3;
  orgModelBox: THREE.Vector3;
  modelCenter: THREE.Vector3;

  // Louver groups
  louverGroup1: THREE.Group | null;
  louverGroup2: THREE.Group | null;
  normalBaiyeTemplate: THREE.Object3D | null;
  ledBaiyeTemplate: THREE.Object3D | null;

  // Face surfaces
  preSetsurface: THREE.Object3D[];
  surfacePartList: SurfacePart[];
  activePart: THREE.Group | null;

  // Dynamic parts
  dynamicColumn: THREE.Group | null;
  dynamicColumnCopy: THREE.Group | null;
  dynamicBeam: THREE.Group | null;
  hengliangSize: THREE.Vector3 | null;

  // Wall mode
  hasWall: boolean;

  // LED visibility
  ledsVisible: boolean;
  ledEmissiveIntensity: number;

  // Scene/background
  currentScene: string | null;
  sceneModels: Map<string, THREE.Group>;
  skybox: THREE.Mesh | null;
  ground: THREE.Mesh | null;
  base: THREE.Mesh | null;
  sceneVisible: boolean;

  // Add/remove icons on surfaces
  addIcons: Map<FaceIDType, THREE.Mesh>;
  removeIcons: Map<FaceIDType, THREE.Mesh>;
  addIconsVisible: boolean;

  // Frame/blind texture tracking
  frameTexture: string;
  blindTexture: string;

  // Virtual wall mesh
  wallMesh: THREE.Mesh | null;

  // Callbacks
  onProgress?: (progress: number) => void;
  onLoad?: () => void;
};

// Scene configuration
export const SCENES = [
  {
    id: 'courtyard',
    name: 'Courtyard-Daytime',
    glb: resolvePublicAssetUrl({ relativePath: 'model/scene/scene1.glb' }),
    hasWall: false,
  },
  {
    id: 'wall',
    name: 'Lean Against The Wall',
    glb: resolvePublicAssetUrl({ relativePath: 'model/scene/scene2.glb' }),
    hasWall: true,
  },
] as const;

export type SceneId = typeof SCENES[number]['id'] | null;

export type PrestigeService = {
  init: (scene: THREE.Scene) => void;
  loadModel: () => Promise<void>;

  // Set skybox and ground references for scene switching. Pass `null` for the
  // skybox to leave it untracked (e.g. when the sky background is hidden).
  setSkyboxAndGround: (skybox: THREE.Mesh | null, ground: THREE.Mesh, base?: THREE.Mesh) => void;

  // Dimension changes
  setWidth: (value: number) => void;
  setDepth: (value: number) => void;
  setHeight: (value: number) => void;

  // Getters
  getRoomCount: () => number;
  getRoomWidth: () => number;
  getWidthValue: () => number;
  getDepthValue: () => number;
  getHeightValue: () => number;
  getModelSize: () => THREE.Vector3;
  getModelCenter: () => THREE.Vector3;
  getGroup: () => THREE.Group;
  getSurfacePartList: () => SurfacePart[];
  getClickableSurfaces: () => THREE.Object3D[];
  getHasWall: () => boolean;
  getCurrentScene: () => string | null;

  // Blind rotation
  setBlindRotation: (degrees: number) => void;

  // Color
  applyFrameColor: (textureImg: string) => void;
  applyBlindColor: (textureImg: string) => void;

  // Wall mounted mode
  setWallMounted: (value: boolean) => void;

  // LED visibility
  toggleLedVisibility: () => void;
  getLedsVisible: () => boolean;
  setLedEmissiveIntensity: (intensity: number) => void;

  // Scene selection
  setScene: (sceneId: SceneId) => Promise<void>;
  setSceneVisibility: (visible: boolean) => void;

  // Accessory management
  canAddPart: (faceId: FaceIDType) => boolean;
  addPart: (faceId: FaceIDType, partType: number) => Promise<void>;
  deletePart: (faceId: FaceIDType) => void;
  deleteAllParts: () => void;
  updatePartStatus: (part: THREE.Object3D) => void;
  getActivePart: () => THREE.Group | null;
  setActivePart: (part: THREE.Group | null) => void;

  // Accessory customization
  setAccessoryLeftRight: (faceId: FaceIDType, isLeft: boolean) => void;
  moveAccessory: (faceId: FaceIDType, value: number) => void;
  moveAccessorySecondary: (faceId: FaceIDType, value: number) => void;
  getAccessoryMovement: (faceId: FaceIDType) => number;
  getAccessorySecondaryMovement: (faceId: FaceIDType) => number;
  getAccessoryLeftRight: (faceId: FaceIDType) => boolean;
  getAccessoryTexture: (faceId: FaceIDType) => string;
  getAccessoryTextureSecondary: (faceId: FaceIDType) => string;
  getAccessoryCapabilities: (partType: number) => AccessoryCapabilities;

  // Merge panel controls
  updateMergeDoorsStatus: (faceId: FaceIDType, doorIndex: number, visible: boolean) => void;
  getMergeDoors: (faceId: FaceIDType) => boolean[];
  getMergeDoorCount: (faceId: FaceIDType) => number;

  // Accessory textures
  setAccessoryTexture: (faceId: FaceIDType, textureImg: string) => void;
  setAccessoryTextureSecondary: (faceId: FaceIDType, textureImg: string) => void;

  // Add/remove icons on faces
  createAddIcons: () => Promise<void>;
  getAddIcons: () => THREE.Mesh[];
  getRemoveIcons: () => THREE.Mesh[];
  updateAddIconVisibility: () => void;
  setAddIconsVisible: (visible: boolean) => void;

  // Cleanup
  dispose: () => void;
};

// Accessory capabilities for customization modal
export type AccessoryCapabilities = {
  canMove: boolean;
  canMoveSecondary: boolean;
  isGlassDoor: boolean;
  isLikeGlassDoor: boolean;
  hasLeftRight: boolean;
  isCurtain: boolean;
  isWpcWall: boolean;
  isMergeType: boolean;
  hasFrameTexture: boolean;
};

const LED_EMISSIVE_COLOR = new THREE.Color(0xfff3cf);
const DEFAULT_LED_EMISSIVE_INTENSITY = 1.15;

// Clone utility with material cloning
const cloneObject = (obj: THREE.Object3D): THREE.Object3D => {
  const clone = obj.clone();
  clone.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      if (Array.isArray(child.material)) {
        child.material = child.material.map(m => m.clone());
      } else {
        child.material = child.material.clone();
      }
    }
  });
  clone.userData = JSON.parse(JSON.stringify(obj.userData));
  return clone;
};

export const createPrestigeService = (): PrestigeService => {
  const state: PrestigeServiceState = {
    scene: null,
    group: new THREE.Group(),
    model: null,
    widthValue: 19,
    depthValue: DEFAULT_DEPTH,
    heightValue: DEFAULT_HEIGHT,
    modelSize: new THREE.Vector3(3, DEFAULT_HEIGHT, DEFAULT_DEPTH),
    orgModelBox: new THREE.Vector3(3, DEFAULT_HEIGHT, DEFAULT_DEPTH),
    modelCenter: new THREE.Vector3(),
    louverGroup1: null,
    louverGroup2: null,
    normalBaiyeTemplate: null,
    ledBaiyeTemplate: null,
    preSetsurface: [],
    surfacePartList: [],
    activePart: null,
    dynamicColumn: null,
    dynamicColumnCopy: null,
    dynamicBeam: null,
    hengliangSize: null,
    hasWall: false,
    ledsVisible: false,
    ledEmissiveIntensity: DEFAULT_LED_EMISSIVE_INTENSITY,
    currentScene: null,
    sceneModels: new Map(),
    skybox: null,
    ground: null,
    base: null,
    sceneVisible: true,
    addIcons: new Map(),
    removeIcons: new Map(),
    addIconsVisible: false,
    frameTexture: '7015.png',
    blindTexture: '7015.png',
    wallMesh: null,
  };

  const LOUVER_SIZE = 0.151;
  const ORG_WIDTH = 3;
  const ORG_DEPTH = 3;
  const ORG_HEIGHT = 2.6;

  const textureLoader = new THREE.TextureLoader();
  let addIconTexture: THREE.Texture | null = null;
  let addIconPromise: Promise<THREE.Texture> | null = null;
  let addIconRequestId = 0;

  let removeIconTexture: THREE.Texture | null = null;
  let removeIconPromise: Promise<THREE.Texture> | null = null;

  let removeIconRequestId = 0;
  const configuredTexturePromises = new Map<string, Promise<THREE.Texture>>();
  const configuredTextures = new Map<string, THREE.Texture>();
  const PART_TEXTURE_PRELOADS: Partial<Record<number, Array<{ textureImg: string; flipY: boolean; repeat: [number, number] }>>> = {
    [PartType.TILTING_HORIZONTAL_SCREEN_PANEL]: [
      { textureImg: 'tiltingHorizontalGrid.jpg', flipY: false, repeat: [8.36, 1] },
    ],
  };
  const UV_CACHE_KEYS = {
    tiltingHorizontalFill: 'tilting-horizontal-fill-zy-0.3-2.1',
    tiltingHorizontalFrame: 'tilting-horizontal-frame',
  } as const;

  const loadAddIconTexture = () => {
    if (addIconTexture) return Promise.resolve(addIconTexture);
    if (addIconPromise) return addIconPromise;
    addIconPromise = new Promise<THREE.Texture>((resolve) => {
      textureLoader.load(resolvePublicAssetUrl({ relativePath: 'images/add.svg' }), (texture) => {
        addIconTexture = texture;
        addIconPromise = null;
        resolve(texture);
      });
    });
    return addIconPromise;
  };

  const loadRemoveIconTexture = () => {
    if (removeIconTexture) return Promise.resolve(removeIconTexture);
    if (removeIconPromise) return removeIconPromise;
    removeIconPromise = new Promise<THREE.Texture>((resolve) => {
      textureLoader.load(resolvePublicAssetUrl({ relativePath: 'images/remove.svg' }), (texture) => {
        removeIconTexture = texture;
        removeIconPromise = null;
        resolve(texture);
      });
    });
    return removeIconPromise;
  };

  const getRalColor = (textureImg: string): string | null => {
    for (const [code, hex] of Object.entries(RAL_COLOR_MAP)) {
      if (textureImg.includes(code)) return hex;
    }
    return null;
  };

  const applyLedEmissionToMesh = ({ mesh, intensity }: { mesh: THREE.Mesh; intensity: number }) => {
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    materials.forEach((material) => {
      if (!(material instanceof THREE.MeshStandardMaterial)) {
        return;
      }
      material.emissive.copy(LED_EMISSIVE_COLOR);
      material.emissiveIntensity = intensity;
      material.needsUpdate = true;
    });
  };

  const isLouverLedMesh = (mesh: THREE.Object3D): boolean => {
    let cur: THREE.Object3D | null = mesh;
    while (cur) {
      const ud = cur.userData as Record<string, unknown> | undefined;
      if (ud?.tag === '灯带百叶') return true;
      cur = cur.parent;
    }
    return false;
  };

  const applyLedEmission = ({ intensity }: { intensity: number }) => {
    state.group.traverse((child) => {
      const userData = child.userData as Record<string, unknown>;
      if (userData.isLed && child instanceof THREE.Mesh && isLouverLedMesh(child)) {
        applyLedEmissionToMesh({ mesh: child, intensity });
      }
    });
  };

  const getTextureMeshContext = ({ mesh }: { mesh: THREE.Mesh }) => {
    let current: THREE.Object3D | null = mesh;
    let faceId: string | null = null;
    let partType: number | null = null;

    while (current) {
      const userData = current.userData as Record<string, unknown> | undefined;
      if (faceId === null && typeof userData?.faceId === 'string') {
        faceId = userData.faceId;
      }
      if (partType === null && typeof userData?.partType === 'number') {
        partType = userData.partType;
      }
      if (faceId !== null && partType !== null) break;
      current = current.parent;
    }

    return {
      faceId,
      partType,
      partTypeName: partType === null ? null : (PART_TYPE_NAMES[partType] ?? String(partType)),
      nodeName: (mesh.userData?.name as string | undefined) ?? mesh.name ?? '',
      materialName: ((mesh.material as THREE.Material | undefined)?.name ?? '') || 'unknown',
    };
  };

  const getConfiguredTextureCacheKey = ({
    textureImg,
    flipY,
    repeat,
  }: {
    textureImg: string;
    flipY: boolean;
    repeat: [number, number];
  }): string => {
    return `${textureImg}|flipY:${String(flipY)}|repeat:${repeat[0]},${repeat[1]}`;
  };

  const ensureUvComputed = ({
    mesh,
    cacheKey,
    computeUv,
  }: {
    mesh: THREE.Mesh;
    cacheKey: string;
    computeUv: () => void;
  }) => {
    const userData = mesh.userData as Record<string, unknown>;
    const uvCache = (userData.__uvCache as Record<string, true> | undefined) ?? {};
    if (uvCache[cacheKey]) return;

    computeUv();
    uvCache[cacheKey] = true;
    userData.__uvCache = uvCache;
  };

  const loadConfiguredTexture = ({
    textureImg,
    flipY,
    repeat,
  }: {
    textureImg: string;
    flipY: boolean;
    repeat: [number, number];
  }): Promise<{ texture: THREE.Texture; textureUrl: string }> => {
    const key = getConfiguredTextureCacheKey({ textureImg, flipY, repeat });
    const existingPromise = configuredTexturePromises.get(key);
    if (existingPromise) {
      return existingPromise.then((texture) => ({ texture, textureUrl: resolvePublicAssetUrl({ relativePath: `texture/${textureImg}` }) }));
    }

    const textureUrl = resolvePublicAssetUrl({ relativePath: `texture/${textureImg}` });
    const promise = new Promise<THREE.Texture>((resolve, reject) => {
      textureLoader.load(
        textureUrl,
        (texture) => {
          texture.flipY = flipY;
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.repeat.set(repeat[0], repeat[1]);
          texture.needsUpdate = true;
          configuredTextures.set(key, texture);
          resolve(texture);
        },
        undefined,
        (error) => {
          configuredTexturePromises.delete(key);
          reject({ error, textureUrl });
        },
      );
    });

    configuredTexturePromises.set(key, promise);
    return promise.then((texture) => ({ texture, textureUrl }));
  };

  const preloadTexturesForPartType = ({
    partType,
  }: {
    partType: number;
  }) => {
    const preloadEntries = PART_TEXTURE_PRELOADS[partType];
    if (!preloadEntries?.length) return;

    preloadEntries.forEach(({ textureImg, flipY, repeat }) => {
      loadConfiguredTexture({ textureImg, flipY, repeat }).catch(({ error, textureUrl }) => {
        console.warn('[PrestigeService] Failed to preload accessory texture', {
          partType,
          partTypeName: PART_TYPE_NAMES[partType] ?? String(partType),
          textureImg,
          textureUrl,
          error,
        });
      });
    });
  };

  const applyTextureOrColor = (
    mesh: THREE.Mesh,
    textureImg: string,
    options?: { flipY?: boolean; repeat?: [number, number]; useColorOnly?: boolean; color?: string }
  ) => {
    const material = mesh.material as THREE.MeshStandardMaterial;
    const ralColor = getRalColor(textureImg);
    const overrideColor = options?.color ?? ralColor ?? null;
    if (options?.useColorOnly && overrideColor) {
      material.color = new THREE.Color(overrideColor);
      material.map = null;
      material.needsUpdate = true;
      return;
    }

    const flipY = options?.flipY !== false;
    const repeat = options?.repeat ?? [1, 1];
    loadConfiguredTexture({ textureImg, flipY, repeat })
      .then(({ texture }) => {
        material.color = new THREE.Color(options?.color ?? 0xffffff);
        material.map = texture;
        material.needsUpdate = true;
      })
      .catch(({ error, textureUrl }) => {
        console.warn('[PrestigeService] Failed to load texture asset', {
          textureImg,
          textureUrl,
          error,
          ...getTextureMeshContext({ mesh }),
        });

        if (ralColor) {
          material.color = new THREE.Color(ralColor);
          material.map = null;
          material.needsUpdate = true;
        }
      });
  };

  const getNodeName = (node: THREE.Object3D): string => {
    return (node.userData?.name as string | undefined) ?? node.name ?? '';
  };

  const updateUV = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 20;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const z = positions[i + 2];
      const u = (x + 1) * scaleFactor * 0.5;
      const v = (z + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4Curtain = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 100;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const z = positions[i + 2];
      const u = (x + 1) * scaleFactor;
      const v = (z + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4CurtainFrame = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 20;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const z = positions[i + 2];
      const u = (x + 1) * scaleFactor;
      const v = (z + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4HorizontalGrid = (mesh: THREE.Mesh, scaleY = 0.2) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const positions = geometry.attributes.position.array as Float32Array;
    let mx = positions[0], my = positions[1], mz = positions[2];
    let mX = positions[0], mY = positions[1], mZ = positions[2];
    for (let i = 0; i < positions.length; i += 3) {
      mx = Math.min(mx, positions[i]);
      my = Math.min(my, positions[i + 1]);
      mz = Math.min(mz, positions[i + 2]);
      mX = Math.max(mX, positions[i]);
      mY = Math.max(mY, positions[i + 1]);
      mZ = Math.max(mZ, positions[i + 2]);
    }
    const leny = mY - my;
    const lenz = mZ - mz;
    for (let i = 0; i < positions.length; i += 3) {
      const y = positions[i + 1];
      const z = positions[i + 2];
      const u = (z - mz) / lenz;
      const v = (y - my) / leny;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4VerticalGrid = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 2;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const z = positions[i + 2];
      const u = (x + 1) * scaleFactor;
      const v = (z + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4DecoGrid = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 2;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const z = positions[i + 2];
      const u = (x + 1) * scaleFactor;
      const v = (z + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4WpcFencing2 = (mesh: THREE.Mesh, scaleY = 0.2) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const positions = geometry.attributes.position.array as Float32Array;
    let mx = positions[0], my = positions[1], mz = positions[2];
    let mX = positions[0], mY = positions[1], mZ = positions[2];
    for (let i = 0; i < positions.length; i += 3) {
      mx = Math.min(mx, positions[i]);
      my = Math.min(my, positions[i + 1]);
      mz = Math.min(mz, positions[i + 2]);
      mX = Math.max(mX, positions[i]);
      mY = Math.max(mY, positions[i + 1]);
      mZ = Math.max(mZ, positions[i + 2]);
    }
    const leny = mY - my;
    const lenz = mZ - mz;
    for (let i = 0; i < positions.length; i += 3) {
      const y = positions[i + 1];
      const z = positions[i + 2];
      let u = (z - mz) / lenz;
      let v = (y - my) / leny;
      u *= 0.2;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4WpcFencing = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 6;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const z = positions[i + 2];
      const u = (x + 1) * scaleFactor;
      const v = (z + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4GlassDoor4 = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 10;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const u = (x + 1) * scaleFactor;
      const v = (y + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4GlassDoor2 = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 10;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const u = (x + 1) * scaleFactor;
      const v = (y + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4WpcWall2 = (mesh: THREE.Mesh, scaleY = 0.2) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const positions = geometry.attributes.position.array as Float32Array;
    let mx = positions[0], my = positions[1], mz = positions[2];
    let mX = positions[0], mY = positions[1], mZ = positions[2];
    for (let i = 0; i < positions.length; i += 3) {
      mx = Math.min(mx, positions[i]);
      my = Math.min(my, positions[i + 1]);
      mz = Math.min(mz, positions[i + 2]);
      mX = Math.max(mX, positions[i]);
      mY = Math.max(mY, positions[i + 1]);
      mZ = Math.max(mZ, positions[i + 2]);
    }
    const lenx = mX - mx;
    const leny = mY - my;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const u = (x - mx) / lenx;
      const v = (y - my) / leny;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4WpcWall = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 2;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const u = (x + 1) * scaleFactor;
      const v = (y + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateUV4WpcVertical = (mesh: THREE.Mesh, scaleY = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const scaleFactor = 6;
    const positions = geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const z = positions[i + 2];
      const u = (x + 1) * scaleFactor;
      const v = (z + 1) * scaleFactor;
      geometry.attributes.uv.setXY(i / 3, u, v * scaleY);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateCmnUV = (mesh: THREE.Mesh, dir: 'xy' | 'xz' | 'yx' | 'yz' | 'zx' | 'zy' = 'xy', scaleU = 1, scaleV = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const positions = geometry.attributes.position.array as Float32Array;
    let mx = positions[0], my = positions[1], mz = positions[2];
    let mX = positions[0], mY = positions[1], mZ = positions[2];
    for (let i = 0; i < positions.length; i += 3) {
      mx = Math.min(mx, positions[i]);
      my = Math.min(my, positions[i + 1]);
      mz = Math.min(mz, positions[i + 2]);
      mX = Math.max(mX, positions[i]);
      mY = Math.max(mY, positions[i + 1]);
      mZ = Math.max(mZ, positions[i + 2]);
    }
    const lenx = mX - mx;
    const leny = mY - my;
    const lenz = mZ - mz;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const z = positions[i + 2];
      const ux = (x - mx) / lenx;
      const uy = (y - my) / leny;
      const uz = (z - mz) / lenz;
      const vx = (x - mx) / lenx;
      const vy = (y - my) / leny;
      const vz = (z - mz) / lenz;
      let u = scaleU;
      let v = scaleV;
      if (dir === 'xy') { u *= ux; v *= vy; }
      else if (dir === 'xz') { u *= ux; v *= vz; }
      else if (dir === 'yx') { u *= uy; v *= vx; }
      else if (dir === 'yz') { u *= uy; v *= vz; }
      else if (dir === 'zx') { u *= uz; v *= vx; }
      else { u *= uz; v *= vy; }
      geometry.attributes.uv.setXY(i / 3, u, v);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  const updateCmnUV2 = (mesh: THREE.Mesh, scaleU = 1, scaleV = 1) => {
    const geometry = mesh.geometry;
    const uv = new Float32Array(geometry.attributes.position.count * 2);
    geometry.attributes.uv = new THREE.BufferAttribute(uv, 2);
    const positions = geometry.attributes.position.array as Float32Array;
    let mx = positions[0], my = positions[1], mz = positions[2];
    let mX = positions[0], mY = positions[1], mZ = positions[2];
    for (let i = 0; i < positions.length; i += 3) {
      mx = Math.min(mx, positions[i]);
      my = Math.min(my, positions[i + 1]);
      mz = Math.min(mz, positions[i + 2]);
      mX = Math.max(mX, positions[i]);
      mY = Math.max(mY, positions[i + 1]);
      mZ = Math.max(mZ, positions[i + 2]);
    }
    const lenx = mX - mx;
    const leny = mY - my;
    const lenz = mZ - mz;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const z = positions[i + 2];
      const ux = (x - mx) / lenx;
      const uy = (y - my) / leny;
      const Mz = (mZ - z) / lenz;
      let u = scaleU;
      let v = scaleV;
      u = u * ux * 0.3 + u * Mz * 0.7;
      v *= uy;
      geometry.attributes.uv.setXY(i / 3, u, v);
    }
    geometry.attributes.uv.needsUpdate = true;
  };

  // Get room count based on width value
  const getRoomCount = (): number => {
    return state.widthValue < 39 ? 1 : 2;
  };

  // Total louvers across all rooms for the current widthValue.
  //   1-room: equals widthValue (19..38 louvers in the single room).
  //   2-room: equals widthValue - 1 (so widthValue=39 → 38 louvers symmetric,
  //   widthValue=40 → 39 asymmetric, …, widthValue=79 → 78 symmetric).
  const getTotalLouverCount = (): number => {
    const w = state.widthValue;
    if (w < 39) return w;
    return w - 1;
  };

  // Per-room louver counts in 2-room mode (room 1 gets the extra louver
  // when the total is odd). For 1-room mode room 2 is 0.
  const getRoom1LouverCount = (): number => {
    const w = state.widthValue;
    if (w < 39) return w;
    return Math.ceil(getTotalLouverCount() / 2);
  };

  const getRoom2LouverCount = (): number => {
    if (state.widthValue < 39) return 0;
    return Math.floor(getTotalLouverCount() / 2);
  };

  // Convert a louver count into a room width in model meters. Base of 2.98m
  // matches the 2-room base; 1-room mode uses ORG_WIDTH (3.0m) instead.
  const louverCountToTwoRoomWidth = (count: number): number =>
    2.98 + (count - 19) * LOUVER_SIZE;

  const getRoom1Width = (): number => {
    if (state.widthValue < 39) {
      return ORG_WIDTH + (state.widthValue - 19) * LOUVER_SIZE;
    }
    return louverCountToTwoRoomWidth(getRoom1LouverCount());
  };

  const getRoom2Width = (): number => {
    if (state.widthValue < 39) return 0;
    return louverCountToTwoRoomWidth(getRoom2LouverCount());
  };

  const getModelTotalWidth = (): number => {
    if (state.widthValue < 39) return getRoom1Width();
    return getRoom1Width() + getRoom2Width();
  };

  // Legacy helper kept for code paths that don't need exact asymmetry; returns
  // room 1's width in 2-room mode (room 2 may be ±LOUVER_SIZE).
  const getRoomWidth = (): number => getRoom1Width();

  // Initialize scene
  const init = (scene: THREE.Scene) => {
    state.scene = scene;
    scene.add(state.group);
  };

  // Update louver visibility based on width
  const updateBaiyeVisibility = () => {
    const width = state.widthValue;
    const count1 = getRoom1LouverCount();
    const count2 = getRoom2LouverCount();
    // For LED placement tables we still key by per-room count; pick the larger.
    const ledKeyCount = Math.max(count1, count2);

    const baiyes1: THREE.Object3D[] = [];
    const baiyes2: THREE.Object3D[] = [];
    const ledBaiyes: Record<string, THREE.Object3D> = {};

    state.group.traverse((child) => {
      const userData = child.userData as Record<string, unknown>;

      if (userData.group === '1房百叶') state.louverGroup1 = child as THREE.Group;
      if (userData.group === '2房百叶') state.louverGroup2 = child as THREE.Group;

      if (userData.tag === '普通百叶') {
        const seq = userData.seq as number;
        const room = userData.room as number;
        const roomCount = room === 1 ? count1 : count2;
        child.visible = seq <= roomCount;
        if (child.visible) {
          child.position.x = room === 1 ? seq * LOUVER_SIZE : -seq * LOUVER_SIZE;
        }
        if (room === 1) baiyes1.push(child);
        else baiyes2.push(child);
      }

      if (userData.tag === '灯带百叶') {
        const room = userData.room as number;
        const seq = userData.seq as number;
        ledBaiyes[`${room}-${seq}`] = child;
        child.visible = false;
      }
    });

    // Place LED louvers based on the per-room count — only when the Louver LED kit is enabled
    const ledPositions = LED_POSITIONS[ledKeyCount];
    if (ledPositions && state.ledsVisible) {
      const [room1Positions, room2Positions] = ledPositions;

      room1Positions.forEach((pos, idx) => {
        const ledBaiye = ledBaiyes[`1-${idx + 1}`];
        const normalBaiye = baiyes1.find(b => (b.userData as Record<string, unknown>).seq === pos);
        if (ledBaiye && normalBaiye) {
          ledBaiye.position.x = normalBaiye.position.x;
          normalBaiye.visible = false;
          ledBaiye.visible = true;
        }
      });

      room2Positions.forEach((pos, idx) => {
        const ledBaiye = ledBaiyes[`2-${idx + 1}`];
        const normalBaiye = baiyes2.find(b => (b.userData as Record<string, unknown>).seq === pos);
        if (ledBaiye && normalBaiye) {
          ledBaiye.position.x = normalBaiye.position.x;
          normalBaiye.visible = false;
          ledBaiye.visible = true;
        }
      });
    }

    // Position louver group centers
    if (state.louverGroup1?.children[0]) {
      state.louverGroup1.children[0].position.x = width < 39
        ? (-count1 * LOUVER_SIZE / 2 - LOUVER_SIZE / 2)
        : (-count1 * LOUVER_SIZE / 2 - 0.06);
    }
    if (state.louverGroup2?.children[0]) {
      state.louverGroup2.children[0].position.x = count2 * LOUVER_SIZE / 2 + 0.06;
    }
    if (state.louverGroup2) {
      state.louverGroup2.visible = width > 38;
    }
  };

  const applyCurtainTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    if (!texture) return;

    part.traverse((child) => {
      if (child instanceof THREE.Mesh && getNodeName(child) === 'Rectangle2059403192') {
        child.material.opacity = 0.95;
        child.material.transparent = true;
        updateUV4Curtain(child, 1);
        applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 1] });
      }
    });

    if (texture2) {
      part.traverse((child) => {
        if (
          child instanceof THREE.Mesh &&
          (getNodeName(child) === 'Rectangle2059403187' || getNodeName(child) === '对象037')
        ) {
          updateUV4CurtainFrame(child, 1);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyHorizontalGridTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    const frames = ['Line1169_029', 'Line1169_030', 'Line1169_031'];
    if (texture) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && !frames.includes(getNodeName(child))) {
          updateCmnUV(child, 'zy', 0.3, 2.1);
          applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && frames.includes(getNodeName(child))) {
          updateUV4HorizontalGrid(child);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyVerticalGridTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    const frames = ['Circle024_013', 'Circle024_001', 'Circle024_002'];
    if (texture) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && !frames.includes(getNodeName(child))) {
          updateCmnUV2(child);
          applyTextureOrColor(child, texture, { flipY: false, repeat: [0.5, 10], useColorOnly: true });
        }
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && frames.includes(getNodeName(child))) {
          updateUV4VerticalGrid(child);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyDecoATextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    if (texture) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && getNodeName(child) === 'Circle030_003') {
          updateCmnUV(child, 'xy', 1, 1);
          applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 3], useColorOnly: true });
        }
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && getNodeName(child) !== 'Circle030_003') {
          updateUV4DecoGrid(child);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyDecoYuguwenTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    if (texture) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && getNodeName(child) === 'Circle025_004') {
          updateCmnUV(child, 'xy', 1, 1);
          applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 3], useColorOnly: true });
        }
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && getNodeName(child) !== 'Circle025_004') {
          updateUV4DecoGrid(child);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyDecoGridTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    if (texture) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && getNodeName(child) === 'Circle032_003') {
          updateCmnUV(child, 'xy', 1, 1);
          applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 3], useColorOnly: true });
        }
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && getNodeName(child) !== 'Circle032_003') {
          updateUV4DecoGrid(child);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyTiltingHorizontalTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    const frames = ['01_003', '01_004', '01_002'];
    if (texture) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && !frames.includes(getNodeName(child))) {
          ensureUvComputed({
            mesh: child,
            cacheKey: UV_CACHE_KEYS.tiltingHorizontalFill,
            computeUv: () => updateCmnUV(child, 'zy', 0.3, 2.1),
          });
          applyTextureOrColor(child, texture, { flipY: false, repeat: [8.36, 1], useColorOnly: true });
        }
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && frames.includes(getNodeName(child))) {
          ensureUvComputed({
            mesh: child,
            cacheKey: UV_CACHE_KEYS.tiltingHorizontalFrame,
            computeUv: () => updateUV4DecoGrid(child),
          });
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyWpcFencingTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    const frames = ['Circle055_001', 'Circle055_002', 'Circle055_003'];
    if (texture) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && !frames.includes(getNodeName(child))) {
          updateUV4WpcFencing2(child, 1);
          applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && frames.includes(getNodeName(child))) {
          updateUV4WpcFencing(child);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyGlassDoor4Textures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    if (!texture) return;
    const names = [
      'Line2967', 'Line2968', 'Rectangle2059403228', 'Rectangle2059403229', 'Line2969', 'Line2970',
      'Rectangle2059403218', 'Rectangle2059403220', 'Rectangle2059403222', 'Rectangle2059403224',
      'Rectangle2059403216', 'Rectangle2059403225', 'Rectangle2059403226', 'Rectangle2059403227',
    ];
    part.traverse((child) => {
      if (child instanceof THREE.Mesh && names.includes(getNodeName(child))) {
        updateUV4GlassDoor4(child);
        applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 1], useColorOnly: true });
      }
    });
  };

  const applyGlassDoor2Textures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    if (!texture) return;
    const glasses = [
      'bl_004', 'bl_008', 'bl_002', 'bl_010', 'bl_014', 'bl_012', 'Rectangle017', 'Rectangle016', 'Rectangle019', 'Rectangle021',
    ];
    part.traverse((child) => {
      if (child instanceof THREE.Mesh && !glasses.includes(getNodeName(child))) {
        updateUV4GlassDoor2(child);
        applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 1], useColorOnly: true });
      }
    });
  };

  const applyFabricTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    if (!texture) return;
    part.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material?.name !== '布艺') {
        updateUV4WpcWall(child);
        applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 1], useColorOnly: true });
      }
    });
  };

  const applyWpcWallTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    if (!texture) return;
    const isWoodgrain = texture === 'tiltingHorizontalGrid2.jpg';
    part.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material?.name === '木塑墙板') {
        if (isWoodgrain) updateUV4WpcWall2(child);
        else updateUV4WpcWall(child);
        applyTextureOrColor(child, texture, { flipY: false, repeat: isWoodgrain ? [6, 20] : [1, 1] });
      }
    });
    part.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material?.name === '木塑墙板2') {
        if (isWoodgrain) updateUV4WpcWall2(child);
        else updateUV4WpcWall(child);
        applyTextureOrColor(child, texture, {
          flipY: false,
          repeat: isWoodgrain ? [6, 20] : [1, 1],
          color: '#cccccc',
        });
      }
    });
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material?.name === '立柱7015') {
          updateUV4WpcWall(child);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  /**
   * Walk one freshly-loaded shutter sub-model (2-track or 3-track) and tag
   * every mesh with the metadata the animation + texture code needs:
   *
   *   userData.trackCount      — 2 or 3 (which sub-model this mesh belongs to)
   *   userData.door            — 1..N panel index, set on the moving doors only.
   *                              3-track: name prefix 02→1, 03→2, 04→3, 05→4.
   *                              2-track: name prefix 02→1, 03→2.
   *                              (01 / 04 / 06 prefixes are the fixed outer
   *                              frame — no `door` set.)
   *   userData.minX            — original local x position (anchor for sliding)
   *   userData.isShutterBlade  — true for the rotating louver blades
   *                              (factory naming: contains "转动格栅")
   *
   * Must be called BEFORE any animation runs so `minX` captures the authored
   * pose and rotation/translation start from a known baseline.
   */
  const tagSlidingAdjustableShutterMeshes = (
    subModel: THREE.Object3D,
    trackCount: SlidingAdjustableShutterTrackCount,
  ): void => {
    const is3Track = trackCount === 3;
    subModel.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const userData = child.userData as Record<string, unknown>;

      userData.trackCount = trackCount;
      userData.minX = child.position.x;
      userData.isShutterBlade = (child.name || '').includes('转动格栅');

      // Door-prefix parsing (regex matches "01-…", "02_…", etc.)
      const match = (child.name || '').match(/^(\d+)[-_]/);
      if (match) {
        const prefix = parseInt(match[1], 10);
        if (is3Track) {
          // 3 tracks: 02→1, 03→2, 04→3, 05→4 (01 / 06 are the fixed frame)
          if (prefix >= 2 && prefix <= 5) userData.door = prefix - 1;
        } else {
          // 2 tracks: 02→1, 03→2 (01 / 04 are the fixed frame)
          if (prefix === 2) userData.door = 1;
          if (prefix === 3) userData.door = 2;
        }
      }
    });
  };

  const applySlidingAdjustableShutterTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    part.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const userData = child.userData as Record<string, unknown>;
      if (userData.shutterMaterialCloned) return;

      child.material = Array.isArray(child.material)
        ? child.material.map((material) => material.clone())
        : child.material.clone();
      userData.shutterMaterialCloned = true;
    });

    if (texture) {
      part.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        if ((child.userData as Record<string, unknown>).isShutterBlade !== true) return;
        applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 1], useColorOnly: true });
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        if ((child.userData as Record<string, unknown>).isShutterBlade !== false) return;
        applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
      });
    }
  };

  const applyWpcVerticalTextures = (part: THREE.Group) => {
    const texture = part.userData.data?.texture as string | undefined;
    const texture2 = part.userData.data?.texture2 as string | undefined;
    if (texture) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && /^Rectangle33/.test(getNodeName(child))) {
          updateCmnUV2(child);
          applyTextureOrColor(child, texture, { flipY: false, repeat: [1, 10] });
        }
      });
    }
    if (texture2) {
      part.traverse((child) => {
        if (child instanceof THREE.Mesh && !/^Rectangle33/.test(getNodeName(child))) {
          updateUV4WpcVertical(child);
          applyTextureOrColor(child, texture2, { flipY: false, repeat: [1, 1], useColorOnly: true });
        }
      });
    }
  };

  const applyAccessoryTextures = (part: THREE.Group) => {
    const partType = part.userData.partType as number | undefined;
    if (!partType) return;
    if (partType === PartType.MANUAL_WIND_SCREEN || partType === PartType.MOTORIZED_WIND_SCREEN) {
      applyCurtainTextures(part);
    } else if (partType === PartType.HORIZONTAL_SCREEN_PANEL) {
      applyHorizontalGridTextures(part);
    } else if (partType === PartType.VERTICAL_SCREEN_PANEL) {
      applyVerticalGridTextures(part);
    } else if (partType === PartType.DECO_A) {
      applyDecoATextures(part);
    } else if (partType === PartType.DECO_YUGUWEN) {
      applyDecoYuguwenTextures(part);
    } else if (partType === PartType.DECO_SCREEN) {
      applyDecoGridTextures(part);
    } else if (partType === PartType.TILTING_HORIZONTAL_SCREEN_PANEL) {
      applyTiltingHorizontalTextures(part);
    } else if (partType === PartType.WPC_FENCING_SCREEN) {
      applyWpcFencingTextures(part);
    } else if (partType === PartType.FRAMELESS_GLASS_DOOR) {
      applyGlassDoor4Textures(part);
    } else if (partType === PartType.SLIMLINE_FRAME_GLASS_DOOR) {
      applyGlassDoor2Textures(part);
    } else if (partType === PartType.SLIDING_DOOR_FABRIC) {
      applyFabricTextures(part);
    } else if (partType === PartType.WPC_WALL) {
      applyWpcWallTextures(part);
    } else if (partType === PartType.VERTICAL_WPC_DECORATION_SLAT) {
      applyWpcVerticalTextures(part);
    } else if (partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL) {
      applySlidingAdjustableShutterTextures(part);
    }
  };

  const createWallMesh = () => {
    if (!state.scene) return;
    if (state.wallMesh) {
      state.group.remove(state.wallMesh);
      state.wallMesh.geometry.dispose();
      (state.wallMesh.material as THREE.Material).dispose();
      state.wallMesh = null;
    }

    const wallGeometry = new THREE.BoxGeometry(
      state.modelSize.x * 1.5,
      state.modelSize.y * 1.5,
      0.1
    );
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
    });
    const wall = new THREE.Mesh(wallGeometry, wallMaterial);
    wall.position.set(
      0,
      state.modelCenter.y,
      state.modelCenter.z - state.modelSize.z / 2 - 0.01
    );
    wall.userData.orgWidth = state.modelSize.x * 1.5;
    wall.userData.orgHeight = state.modelSize.y * 1.5;
    wall.userData.isWall = true;
    wall.visible = false;
    state.wallMesh = wall;
    state.group.add(wall);
  };

  const updateWallMeshTransform = () => {
    if (!state.wallMesh) return;
    const scaleX = state.modelSize.x / state.orgModelBox.x;
    const scaleY = state.modelSize.y / state.orgModelBox.y;
    state.wallMesh.scale.set(scaleX, scaleY, 1);
    state.wallMesh.position.set(
      0,
      state.modelCenter.y,
      state.modelCenter.z - state.modelSize.z / 2 - 0.01
    );
  };

  const updateWallVisibility = () => {
    // TODO: Keep wall for shadows?
    console.log('updateWallVisibility', state.wallMesh);
    if (!state.wallMesh) return;
    const sceneModel = state.currentScene ? state.sceneModels.get(state.currentScene) : null;
    const sceneHasWall = sceneModel?.userData?.hasWall === true && sceneModel.visible === true;
    state.wallMesh.visible = state.hasWall && !sceneHasWall;
    // state.wallMesh.visible = false; // Disabled for testing
  };

  // Load the main model and sub-models
  const loadModel = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      let modelReady = false;
      let columnReady = false;
      let beamReady = false;
      let lizhuNZ = 0;
      let lizhuSZ = 0;

      state.surfacePartList = [];
      state.preSetsurface = [];

      loader.load(
        resolvePublicAssetUrl({ relativePath: 'models/prestige/model.glb' }),
        (gltf) => {
          const model = gltf.scene;
          state.model = model;
          state.group.add(model);

          const box = new THREE.Box3().setFromObject(model);
          state.orgModelBox = box.getSize(new THREE.Vector3());
          state.modelSize.copy(state.orgModelBox);
          state.modelCenter = box.getCenter(new THREE.Vector3());

          let normalBaiye: THREE.Object3D | null = null;
          let ledBaiye: THREE.Object3D | null = null;

          // Initialize model parts
          model.traverse((child: THREE.Object3D) => {
            const userData = child.userData as Record<string, unknown>;
            const tag = userData.tag as string | undefined;
            const dir = userData.dir as string | undefined;

            if (tag && tag !== '顶灯带') {
              userData.orgX = child.position.x;
              userData.orgY = child.position.y;
              userData.orgZ = child.position.z;
            }

            if (tag === '立柱中段') {
              if (dir === '东北') lizhuNZ = child.position.z;
              if (dir === '东南') lizhuSZ = child.position.z;
              const size = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3());
              userData.orgHeight = size.y;
            } else if ((tag === '横梁' && (dir === '东' || dir === '西')) || tag === '百叶') {
              const size = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3());
              userData.orgDepth = size.z;
            } else if ((tag === '横梁' && (dir === '南' || dir === '北')) || tag === '横梁卡条') {
              const size = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3());
              userData.orgWidth = size.x;
            } else if (tag === '樑灯带') {
              const size = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3());
              if (dir === '东' || dir === '西') userData.orgDepth = size.z;
              else userData.orgWidth = size.x;
            }

            // Handle LED materials: perimeter LEDs always on, louver LEDs gated by toggle
            if (userData.isLed && child instanceof THREE.Mesh) {
              const isLouverLed = isLouverLedMesh(child);
              applyLedEmissionToMesh({
                mesh: child,
                intensity: isLouverLed ? 0 : state.ledEmissiveIntensity,
              });
            }

            // Setup face surfaces
            if (tag === '东面' && child instanceof THREE.Mesh) {
              const size = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3());
              userData.orgHeight = size.y;
              userData.orgDepth = size.z;
              userData.faceId = FaceID.East;
              userData.surface = 'east';
              child.material.transparent = true;
              child.material.opacity = 0;
              state.preSetsurface.push(child);
              state.surfacePartList.push({ faceId: FaceID.East, visible: true, partType: null, part: null });
            } else if (tag === '西面' && child instanceof THREE.Mesh) {
              const size = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3());
              userData.orgHeight = size.y;
              userData.orgDepth = size.z;
              userData.faceId = FaceID.West;
              userData.surface = 'west';
              child.material.transparent = true;
              child.material.opacity = 0;
              state.preSetsurface.push(child);
              state.surfacePartList.push({ faceId: FaceID.West, visible: true, partType: null, part: null });
            } else if (tag === '南面' && child instanceof THREE.Mesh) {
              const size = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3());
              userData.orgHeight = size.y;
              userData.orgWidth = size.x;
              userData.tag = '南面0';
              userData.group = '1房面';
              userData.faceId = FaceID.South0;
              userData.surface = 'south';
              child.material.transparent = true;
              child.material.opacity = 0;
              state.preSetsurface.push(child);
              state.surfacePartList.push({ faceId: FaceID.South0, visible: true, partType: null, part: null });

              // Clone for room 2
              const clone = cloneObject(child);
              clone.userData.tag = '南面1';
              clone.userData.group = '2房面';
              clone.userData.faceId = FaceID.South1;
              clone.userData.surface = 'south';
              state.group.add(clone);
              state.preSetsurface.push(clone);
              state.surfacePartList.push({ faceId: FaceID.South1, visible: false, partType: null, part: null });
            } else if (tag === '北面' && child instanceof THREE.Mesh) {
              const size = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3());
              userData.orgHeight = size.y;
              userData.orgWidth = size.x;
              userData.tag = '北面0';
              userData.group = '1房面';
              userData.faceId = FaceID.North0;
              userData.surface = 'north';
              child.material.transparent = true;
              child.material.opacity = 0;
              state.preSetsurface.push(child);
              state.surfacePartList.push({ faceId: FaceID.North0, visible: true, partType: null, part: null });

              // Clone for room 2
              const clone = cloneObject(child);
              clone.userData.tag = '北面1';
              clone.userData.group = '2房面';
              clone.userData.faceId = FaceID.North1;
              clone.userData.surface = 'north';
              state.group.add(clone);
              state.preSetsurface.push(clone);
              state.surfacePartList.push({ faceId: FaceID.North1, visible: false, partType: null, part: null });
            }

            if (tag === '普通百叶') normalBaiye = child;
            if (tag === '灯带百叶') ledBaiye = child;

            // Enable shadows
            if (child instanceof THREE.Mesh && !userData.faceId) {
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          // Setup louver groups
          if (normalBaiye !== null && ledBaiye !== null) {
            const normalTemplate: THREE.Object3D = normalBaiye;
            const ledTemplate: THREE.Object3D = ledBaiye;

            const normalSize = new THREE.Box3().setFromObject(normalTemplate).getSize(new THREE.Vector3());
            const ledSize = new THREE.Box3().setFromObject(ledTemplate).getSize(new THREE.Vector3());
            (normalTemplate.userData as Record<string, unknown>).orgDepth = normalSize.z;
            (ledTemplate.userData as Record<string, unknown>).orgDepth = ledSize.z;

            const groupBaiye1 = new THREE.Group();
            const groupInner1 = new THREE.Group();
            const groupBaiye2 = new THREE.Group();
            const groupInner2 = new THREE.Group();

            if (normalTemplate.parent) normalTemplate.parent.remove(normalTemplate);
            if (ledTemplate.parent) ledTemplate.parent.remove(ledTemplate);

            groupBaiye1.add(groupInner1);
            groupBaiye2.add(groupInner2);

            // Clone LED louvers (4 per room)
            for (let i = 0; i < 4; i++) {
              let clone = cloneObject(ledTemplate);
              clone.userData.seq = i + 1;
              clone.userData.room = 1;
              clone.userData.tag = '灯带百叶';
              clone.visible = false;
              groupInner1.add(clone);

              clone = cloneObject(ledTemplate);
              clone.userData.seq = i + 1;
              clone.userData.room = 2;
              clone.userData.tag = '灯带百叶';
              clone.visible = false;
              groupInner2.add(clone);
            }

            // Clone normal louvers (40 per room — supports up to widthValue=79 in 2-room mode)
            for (let i = 0; i < 40; i++) {
              let clone = cloneObject(normalTemplate);
              clone.userData.seq = i + 1;
              clone.userData.room = 1;
              clone.userData.tag = '普通百叶';
              clone.visible = false;
              groupInner1.add(clone);

              clone = cloneObject(normalTemplate);
              clone.userData.seq = i + 1;
              clone.userData.room = 2;
              clone.userData.tag = '普通百叶';
              clone.visible = false;
              groupInner2.add(clone);
            }

            groupBaiye1.userData.group = '1房百叶';
            groupBaiye2.userData.group = '2房百叶';
            groupBaiye2.visible = false;

            state.louverGroup1 = groupBaiye1;
            state.louverGroup2 = groupBaiye2;
            state.group.add(groupBaiye1);
            state.group.add(groupBaiye2);

            updateBaiyeVisibility();
          }

          createWallMesh();

          modelReady = true;
          checkAllReady();
        },
        (xhr) => {
          const progress = (xhr.loaded / xhr.total) * 100;
          state.onProgress?.(progress);

          // Load sub-models when main model is loaded
          if (progress >= 100) {
            // Load dynamic column
            loader.load(resolvePublicAssetUrl({ relativePath: 'models/prestige/column.glb' }), (gltf) => {
              const columnModel = gltf.scene;
              columnModel.userData.group = '动态立柱0';

              columnModel.traverse((item: THREE.Object3D) => {
                if (item instanceof THREE.Mesh) {
                  item.castShadow = true;
                  item.receiveShadow = true;
                }
                if (/立柱中段/.test(item.userData.tag as string || '')) {
                  const size = new THREE.Box3().setFromObject(item).getSize(new THREE.Vector3());
                  item.userData.orgHeight = size.y;
                }
                if (item.userData.tag) {
                  item.userData.orgY = item.position.y;
                }
              });

              const copyModel = cloneObject(columnModel);

              columnModel.userData.dir = '北';
              columnModel.position.z = lizhuNZ;
              columnModel.userData.orgZ = columnModel.position.z;
              columnModel.rotation.y = Math.PI;

              copyModel.userData.dir = '南';
              copyModel.position.z = lizhuSZ;
              copyModel.userData.orgZ = copyModel.position.z;

              columnModel.visible = false;
              copyModel.visible = false;

              state.dynamicColumn = columnModel as THREE.Group;
              state.dynamicColumnCopy = copyModel as THREE.Group;
              state.group.add(columnModel);
              state.group.add(copyModel);

              columnReady = true;
              checkAllReady();
            });

            // Load dynamic beam
            loader.load(resolvePublicAssetUrl({ relativePath: 'models/prestige/hengliang.glb' }), (gltf) => {
              const beamModel = gltf.scene;

              beamModel.traverse((item: THREE.Object3D) => {
                if (item instanceof THREE.Mesh) {
                  item.castShadow = true;
                  item.receiveShadow = true;
                }
              });

              const beamSize = new THREE.Box3().setFromObject(beamModel).getSize(new THREE.Vector3());
              beamModel.scale.z = 2.87 / beamSize.z;
              state.hengliangSize = beamSize;

              const grp = new THREE.Group();
              grp.position.y = 2.48;
              grp.userData.orgY = grp.position.y;
              grp.userData.orgDepth = 2.87;
              grp.userData.group = '动态横梁0';
              grp.visible = false;
              grp.add(beamModel);

              state.dynamicBeam = grp;
              state.group.add(grp);

              beamReady = true;
              checkAllReady();
            });
          }
        },
        (error) => {
          console.error('Error loading model:', error);
          reject(error);
        }
      );

      const checkAllReady = () => {
        if (modelReady && columnReady && beamReady) {
          state.onLoad?.();
          resolve();
        }
      };
    });
  };

  // Change model width
  const setWidth = (value: number) => {
    state.widthValue = value;

    const roomCount = getRoomCount();
    const room1Width = getRoom1Width();
    const room2Width = getRoom2Width();
    const newModelWidth = getModelTotalWidth();

    if (Math.abs(state.modelSize.x - newModelWidth) < 0.001) return;

    const addLength = newModelWidth - ORG_WIDTH;
    state.modelSize.x = newModelWidth;
    const moveX = addLength / 2;

    // Room center X positions (asymmetric in 2-room mode when total is odd)
    // The geometric model center is at x=0. Room 1 sits on the left, room 2 on the right.
    const room1CenterX =
      roomCount === 2 ? -newModelWidth / 2 + room1Width / 2 : 0;
    const room2CenterX =
      roomCount === 2 ? newModelWidth / 2 - room2Width / 2 : 0;

    state.group.traverse((item) => {
      const userData = item.userData as Record<string, unknown>;
      const tag = userData.tag as string | undefined;
      const group = userData.group as string | undefined;
      const name = userData.name as string | undefined;
      const orgWidth = userData.orgWidth as number | undefined;

      if (!/配件/.test(tag || '')) {
        if (orgWidth) {
          // Per-room face panels scale to their own room width.
          let difLen: number;
          if (/面/.test(tag || '')) {
            const targetRoomWidth =
              group === '2房面' ? room2Width : room1Width;
            difLen = targetRoomWidth - ORG_WIDTH;
          } else {
            difLen = addLength;
          }
          item.scale.x = (orgWidth + difLen) / orgWidth;
          if (tag === '横梁卡条') {
            item.position.x = 0.475 * item.scale.x;
          }
        } else if (userData.orgX !== undefined) {
          if (/变速箱/.test(name || '')) {
            if (item.position.x < 0) {
              item.position.x = item.position.x > 0
                ? ((userData.orgX as number) + moveX)
                : ((userData.orgX as number) - moveX);
            }
          } else if (!/百叶/.test(tag || '')) {
            item.position.x = item.position.x > 0
              ? ((userData.orgX as number) + moveX)
              : ((userData.orgX as number) - moveX);
          }
        }
      }

      if (roomCount === 1) {
        if (/动态横梁/.test(group || '')) item.visible = false;
        if (/变速箱1/.test(name || '')) item.visible = false;
        if (group === '1房百叶') item.position.x = 0;
        if (group === '2房百叶') item.visible = false;
        if (group === '1房面') item.position.x = 0;
        if (group === '2房面') item.visible = false;
      } else if (roomCount === 2) {
        // The boundary between room 1 and room 2 (where the mid-span column
        // and roof beam sit). x = 0 when symmetric, shifts by half a louver
        // when the two rooms have different louver counts.
        const roomBoundaryX = room1CenterX + room1Width / 2;

        if (/动态横梁0/.test(group || '')) {
          item.visible = true;
          item.position.x = roomBoundaryX;
        }
        if (/变速箱1/.test(name || '')) {
          item.visible = true;
          item.position.x = 0.256;
        }
        if (group === '1房百叶') item.position.x = room1CenterX;
        if (group === '2房百叶') {
          item.visible = true;
          item.position.x = room2CenterX;
        }
        if (group === '1房面') item.position.x = room1CenterX;
        if (group === '2房面') item.position.x = room2CenterX;
        // Mid-span dynamic posts sit on the same boundary as the beam above.
        if (/动态立柱/.test(group || '')) {
          item.position.x = roomBoundaryX;
        }
      }
    });

    updateBaiyeVisibility();
    updateColumnSurfacePartVisibility();
    updateWallMeshTransform();
  };

  // Change model depth
  const setDepth = (value: number) => {
    state.depthValue = value;

    const diff = value - ORG_DEPTH;
    state.modelSize.z = value;

    state.group.traverse((item) => {
      const userData = item.userData as Record<string, unknown>;

      if (userData.isPart) {
        updatePartStatus(item);
      } else {
        if (userData.orgDepth) {
          item.scale.z = ((userData.orgDepth as number) + diff) / (userData.orgDepth as number);
        } else if (userData.orgZ !== undefined) {
          item.position.z = item.position.z > 0
            ? ((userData.orgZ as number) + diff / 2)
            : ((userData.orgZ as number) - diff / 2);

          if (/变速箱/.test(userData.name as string || '')) {
            item.position.z += 0.014;
          }
        }
      }
    });

    updateWallMeshTransform();
    updateWallScenePosition();
  };

  // Change model height
  const setHeight = (value: number) => {
    state.heightValue = value;

    const addLength = value - ORG_HEIGHT;
    state.modelSize.y = value;
    const moveY = addLength / 2;

    state.group.traverse((item) => {
      const userData = item.userData as Record<string, unknown>;
      const tag = userData.tag as string | undefined;

      if (/立柱中段/.test(tag || '')) {
        item.scale.y = ((userData.orgHeight as number) + addLength) / (userData.orgHeight as number);
        item.position.y = (userData.orgY as number) + moveY;
      } else if (userData.orgY && (userData.orgY as number) > 1 && !/面|立柱下段|配件/.test(tag || '')) {
        item.position.y = (userData.orgY as number) + addLength;
      }

      if (/面/.test(tag || '')) {
        item.scale.z = ((userData.orgHeight as number) + addLength) / (userData.orgHeight as number);
        item.position.y = (userData.orgY as number) + moveY;
      }

      updatePartStatus(item);
    });

    updateWallMeshTransform();
  };

  // Update column and surface visibility based on room count
  const updateColumnSurfacePartVisibility = () => {
    const roomCount = getRoomCount();
    const hasWall = state.hasWall;

    // Update surface part visibility
    const setVisibility = (faceId: FaceIDType, visible: boolean) => {
      const part = state.surfacePartList.find(p => p.faceId === faceId);
      if (part) part.visible = visible;
    };

    if (roomCount === 1) {
      setVisibility(FaceID.North0, !hasWall);
      setVisibility(FaceID.North1, false);
      setVisibility(FaceID.South1, false);

      state.group.traverse((child) => {
        const userData = child.userData as Record<string, unknown>;
        const tag = userData.tag as string | undefined;
        const group = userData.group as string | undefined;
        const dir = userData.dir as string | undefined;

        if (hasWall) {
          if ((/立柱/.test(group || '') || /立柱/.test(tag || '')) && /北/.test(dir || '')) {
            child.visible = false;
          }
        } else {
          if ((/立柱/.test(group || '') || /立柱/.test(tag || '')) && /东北|西北/.test(dir || '')) {
            child.visible = true;
          }
        }
        if (/动态立柱/.test(group || '')) child.visible = false;
        updatePartStatus(child);
      });
    } else if (roomCount === 2) {
      setVisibility(FaceID.North0, !hasWall);
      setVisibility(FaceID.North1, !hasWall);
      setVisibility(FaceID.South1, true);

      state.group.traverse((child) => {
        const userData = child.userData as Record<string, unknown>;
        const tag = userData.tag as string | undefined;
        const group = userData.group as string | undefined;
        const dir = userData.dir as string | undefined;

        // Two-room legitimately has a mid-span post on the south edge
        // (and also on the north edge when free-standing) — 3 posts per
        // visible side, 6 total in free-standing mode.
        if (hasWall) {
          if ((/立柱/.test(group || '') || /立柱/.test(tag || '')) && /北/.test(dir || '')) {
            child.visible = false;
          }
          if (group === '动态立柱0' && dir === '南') child.visible = true;
          if (group === '动态立柱0' && dir === '北') child.visible = false;
        } else {
          if ((/立柱/.test(group || '') || /立柱/.test(tag || '')) && /东北|西北/.test(dir || '')) {
            child.visible = true;
          }
          if (/动态立柱/.test(group || '')) child.visible = true;
        }
        updatePartStatus(child);
      });
    }
  };

  // Set blind rotation (Prestige uses different rotation formula)
  const setBlindRotation = (degrees: number) => {
    const rotation = (180 - degrees) * Math.PI / 180;

    state.group.traverse((item) => {
      const userData = item.userData as Record<string, unknown>;
      if (/百叶/.test(userData.tag as string || '')) {
        item.rotation.z = rotation;
      }
    });
  };

  // Apply frame texture/color to all frame parts including dynamic columns
  const applyFrameColor = (textureImg: string) => {
    state.frameTexture = textureImg;
    const ralColor = getRalColor(textureImg);
    const frameTags = ['立柱', '横梁', '立柱中段', '横梁卡条', '变速盒', '变速摇杆', '樑灯带'];

    const applyToMesh = (mesh: THREE.Mesh) => {
      if (ralColor) {
        const material = mesh.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color(ralColor);
        material.map = null;
        material.needsUpdate = true;
      } else {
        updateUV(mesh, 1);
        applyTextureOrColor(mesh, textureImg, { flipY: false, repeat: [1, 1] });
      }
    };

    state.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const userData = child.userData as Record<string, unknown>;
        const tag = userData.tag as string | undefined;
        if (tag && frameTags.some(frameTag => tag.includes(frameTag))) {
          applyToMesh(child);
        }
        if (userData.isLedGroup) {
          applyToMesh(child);
        }
      }
    });

    if (state.dynamicColumn) {
      state.dynamicColumn.traverse((child) => {
        if (child instanceof THREE.Mesh) applyToMesh(child);
      });
    }

    if (state.dynamicColumnCopy) {
      state.dynamicColumnCopy.traverse((child) => {
        if (child instanceof THREE.Mesh) applyToMesh(child);
      });
    }

    if (state.dynamicBeam) {
      state.dynamicBeam.traverse((child) => {
        if (child instanceof THREE.Mesh) applyToMesh(child);
      });
    }
  };

  // Apply blind/louver color to all louver meshes
  // Uses Vue's two-step traversal pattern: find groups first, then traverse to meshes
  const applyBlindColor = (textureImg: string) => {
    state.blindTexture = textureImg;
    const ralColor = getRalColor(textureImg);
    const items: THREE.Object3D[] = [];
    const meshes: THREE.Mesh[] = [];

    state.group.traverse((child) => {
      const userData = child.userData as Record<string, unknown>;
      const tag = userData.tag as string || '';
      const dir = userData.dir as string | undefined;

      if (((/百叶/.test(tag) || /动态横梁/.test(tag) || /中间横梁/.test(tag) || /动态横梁/.test(userData.group as string || '')) && !dir) || userData.isCubeTopPartMesh) {
        items.push(child);
      }
    });

    items.forEach((item) => {
      item.traverse((child) => {
        if (child instanceof THREE.Mesh && child.userData.tag !== '动态横梁摇杆皮套') {
          meshes.push(child);
        }
      });
    });

    meshes.forEach((mesh) => {
      if (ralColor) {
        const material = mesh.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color(ralColor);
        material.map = null;
        material.needsUpdate = true;
      } else {
        updateUV(mesh, 1);
        applyTextureOrColor(mesh, textureImg, { flipY: false, repeat: [1, 1] });
      }
    });
  };

  // Check if can add part to face
  const canAddPart = (faceId: FaceIDType): boolean => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    return surfacePart?.visible ?? false;
  };

  // Delete part from face
  const deletePart = (faceId: FaceIDType) => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (surfacePart) {
      surfacePart.partType = null;
      if (surfacePart.part) {
        state.group.remove(surfacePart.part);
        surfacePart.part = null;
      }
    }

    // Also search for any orphaned parts
    let existPart: THREE.Object3D | null = null;
    state.group.traverse((child) => {
      const userData = child.userData as Record<string, unknown>;
      if (userData.tag === '配件' && userData.faceId === faceId) {
        existPart = child;
      }
    });

    if (existPart) {
      state.group.remove(existPart);
    }

    // Clear active part if it was deleted
    if (state.activePart?.userData.faceId === faceId) {
      state.activePart = null;
    }

    updateAddIconVisibility();
  };

  const deleteAllParts = () => {
    const faceIds = state.surfacePartList
      .filter(part => part.partType !== null)
      .map(part => part.faceId);
    faceIds.forEach((faceId) => {
      deletePart(faceId);
    });
  };

  // Wrap part model into a managed group (matching Vue app warpPart)
  const wrapPart = (
    partModel: THREE.Group,
    faceId: FaceIDType,
    partType: number
  ): THREE.Group => {
    const wrapGroup = new THREE.Group();
    const groupInner = new THREE.Group();
    wrapGroup.add(groupInner);

    // Enable shadows on all meshes
    partModel.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
      }
    });

    // Store original model size
    partModel.userData.isPartModel = true;
    partModel.userData.modelSize = new THREE.Box3().setFromObject(partModel).getSize(new THREE.Vector3());

    // Set up wrapper group userData
    wrapGroup.userData.tag = '配件';
    wrapGroup.userData.isPart = true;
    wrapGroup.userData.faceId = faceId;
    wrapGroup.userData.partType = partType;
    wrapGroup.userData.data = {};

    // Determine door type and setup based on part type
    const isMergeType = ([
      PartType.HORIZONTAL_SCREEN_PANEL,
      PartType.VERTICAL_SCREEN_PANEL,
      PartType.DECO_SCREEN,
      PartType.DECO_A,
      PartType.DECO_YUGUWEN,
      PartType.WPC_FENCING_SCREEN,
      PartType.TILTING_HORIZONTAL_SCREEN_PANEL,
    ] as number[]).includes(partType);

    if (isMergeType) {
      wrapGroup.userData.data.texture2 = state.frameTexture;
      wrapGroup.userData.data.texture = state.frameTexture;
      if (partType === PartType.WPC_FENCING_SCREEN) {
        wrapGroup.userData.data.texture = state.frameTexture;
      }
      // Create 6 clones for merge-type doors (3-6 panels)
      wrapGroup.userData.doorType = DoorType.Merge6;
      wrapGroup.userData.doors = [true, true, true, true, true, true];
      wrapGroup.userData.mergeDoors = 3;

      partModel.visible = false;
      for (let i = 0; i < 6; i++) {
        const clone = cloneObject(partModel) as THREE.Group;
        clone.userData.seq = i;
        clone.visible = i < 3;
        groupInner.add(clone);
      }
    } else if (partType === PartType.SLIDING_DOOR_FABRIC) {
      wrapGroup.userData.data.texture = state.frameTexture;
      wrapGroup.userData.doorType = DoorType.SimpleOne;
      partModel.rotateY(Math.PI);
      groupInner.add(partModel);
    } else if (partType === PartType.WPC_WALL) {
      // Initialize default wall finish (Iron Timber)
      wrapGroup.userData.data.texture = 'wpc_sh.jpg';
      wrapGroup.userData.data.texture2 = state.frameTexture;
      wrapGroup.userData.doorType = DoorType.SimpleOne;
      groupInner.scale.x = 3.01 / 4;
      groupInner.add(partModel);
    } else if (partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL) {
      wrapGroup.userData.data.texture = state.frameTexture;
      wrapGroup.userData.data.texture2 = state.frameTexture;
      wrapGroup.userData.doorType = DoorType.SimpleOne;
      groupInner.add(partModel);
    } else if (partType === PartType.VERTICAL_WPC_DECORATION_SLAT) {
      wrapGroup.userData.data.texture2 = state.frameTexture;
      wrapGroup.userData.data.texture = state.frameTexture;
      wrapGroup.userData.doorType = DoorType.SimpleOne;
      groupInner.add(partModel);
    } else {
      // Simple one-piece parts (curtains, glass doors, etc.)
      wrapGroup.userData.doorType = DoorType.SimpleOne;
      if (partType === PartType.MANUAL_WIND_SCREEN || partType === PartType.MOTORIZED_WIND_SCREEN) {
        partModel.position.z = -0.018;
        // Initialize default curtain texture
        wrapGroup.userData.data.texture = 'curtain_black.png';
        wrapGroup.userData.data.texture2 = state.frameTexture;
      } else if (partType === PartType.FRAMELESS_GLASS_DOOR || partType === PartType.SLIMLINE_FRAME_GLASS_DOOR) {
        wrapGroup.userData.data.texture = state.frameTexture;
      }
      groupInner.add(partModel);
    }

    return wrapGroup;
  };

  // Update part inner layout (dynamic door assembly for merge types)
  const updatePartInner = (part: THREE.Group) => {
    const userData = part.userData as Record<string, unknown>;
    if (!userData.isPart || !part.visible) return;

    const doorType = userData.doorType as number;
    if (doorType !== DoorType.Merge6) return;

    const faceId = userData.faceId as FaceIDType;
    const isRoom2InnerFace =
      faceId === FaceID.South1 || faceId === FaceID.North1;
    const count = isRoom2InnerFace
      ? getRoom2LouverCount()
      : getRoom1LouverCount();
    const groupInner = part.children[0] as THREE.Group;

    const faceSize = (faceId === FaceID.East || faceId === FaceID.West)
      ? state.depthValue
      : count;

    const doors = (userData.doors as boolean[] | undefined) ?? [true, true, true, true, true, true];
    userData.doors = doors;
    const singleWidth = (groupInner.children[0]?.userData.modelSize as THREE.Vector3)?.x || 0.93;
    const orgFaceWidth = 2.791;

    let mergeCount: number;
    let positions: number[];

    if (faceSize <= 27) {
      mergeCount = 3;
      positions = [-singleWidth, 0, singleWidth, 0, 0, 0];
    } else if (faceSize <= 30) {
      mergeCount = 4;
      positions = [-singleWidth * 1.5, -singleWidth * 0.5, singleWidth * 0.5, singleWidth * 1.5, 0, 0];
    } else if (faceSize <= 34) {
      mergeCount = 5;
      positions = [-singleWidth * 2, -singleWidth, 0, singleWidth, singleWidth * 2, 0];
    } else {
      mergeCount = 6;
      positions = [-singleWidth * 2.5, -singleWidth * 1.5, -singleWidth * 0.5, singleWidth * 0.5, singleWidth * 1.5, singleWidth * 2.5];
    }

    groupInner.children.forEach((item, idx) => {
      if (idx < mergeCount) {
        item.visible = doors[idx] ?? true;
        item.position.x = positions[idx];
      } else {
        item.visible = false;
        item.position.x = 0;
      }
    });

    userData.mergeDoors = mergeCount;
    groupInner.scale.x = orgFaceWidth / (singleWidth * mergeCount);

    const anyVisible = doors.slice(0, mergeCount).some(Boolean);
    if (!anyVisible) {
      deletePart(faceId);
    }
  };

  const updatePartFabric = (part: THREE.Object3D, isDoor6: boolean) => {
    const userData = part.userData as Record<string, unknown>;
    if (userData.partType !== PartType.SLIDING_DOOR_FABRIC) return;
    part.traverse((child) => {
      if (child.userData.doorGroup === 4) {
        child.visible = !isDoor6;
      } else if (child.userData.doorGroup === 6) {
        child.visible = isDoor6;
      }
    });
  };

  const updatePartSlimlineFrameGlassDoor = (part: THREE.Object3D, isDoor6: boolean) => {
    const userData = part.userData as Record<string, unknown>;
    if (userData.partType !== PartType.SLIMLINE_FRAME_GLASS_DOOR) return;
    part.traverse((child) => {
      if (child.userData.doorGroup === 2) {
        child.visible = !isDoor6;
      } else if (child.userData.doorGroup === 3) {
        child.visible = isDoor6;
      }
    });
  };

  const normalizeFbxSurfacePart = (payload: {
    partModel: THREE.Group;
  }): void => {
    const { partModel } = payload;

    partModel.scale.setScalar(0.001);
    partModel.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(partModel);
    const center = box.getCenter(new THREE.Vector3());

    partModel.position.x -= center.x;
    partModel.position.y -= box.min.y;
    partModel.position.z -= center.z;
    partModel.updateMatrixWorld(true);
  };

  const getSlidingAdjustableShutterTrackCountForFace = ({
    faceId,
  }: {
    faceId: FaceIDType;
  }): SlidingAdjustableShutterTrackCount => {
    const spanMeters = faceId === FaceID.East || faceId === FaceID.West
      ? state.depthValue
      : getRoomWidth();

    return getSlidingAdjustableShutterTrackCountForSpan({ spanMeters });
  };

  const getSurfacePartModelPath = ({
    faceId,
    partType,
  }: {
    faceId: FaceIDType;
    partType: number;
  }): string | undefined => {
    if (partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL) {
      return getSlidingAdjustableShutterModelPath({
        trackCount: getSlidingAdjustableShutterTrackCountForFace({ faceId }),
      });
    }

    return ACCESSORY_MODELS[partType];
  };

  const loadSurfacePartModel = ({
    modelPath,
  }: {
    modelPath: string;
  }): Promise<THREE.Group> => {
    const modelUrl = resolvePublicAssetUrl({ relativePath: `models/surface/${modelPath}` });

    return new Promise((resolve, reject) => {
      if (modelPath.toLowerCase().endsWith('.fbx')) {
        const loader = new FBXLoader();
        loader.load(
          modelUrl,
          (partModel) => {
            normalizeFbxSurfacePart({ partModel });
            resolve(partModel);
          },
          undefined,
          (error) => {
            console.error('Error loading part:', error);
            reject(error);
          }
        );
        return;
      }

      const loader = new GLTFLoader();
      loader.load(
        modelUrl,
        (gltf) => resolve(gltf.scene),
        undefined,
        (error) => {
          console.error('Error loading part:', error);
          reject(error);
        }
      );
    });
  };

  const attachLoadedPart = (payload: {
    faceId: FaceIDType;
    partModel: THREE.Group;
    partType: number;
    resolve: () => void;
  }): void => {
    const { faceId, partModel, partType, resolve } = payload;
    const wrappedPart = wrapPart(partModel, faceId, partType);

    // Build merged layout first (affects true bounds for multi-panel parts)
    updatePartInner(wrappedPart);

    // Calculate original size for scaling
    const size = new THREE.Box3().setFromObject(wrappedPart).getSize(new THREE.Vector3());
    wrappedPart.userData.orgSize = size;
    wrappedPart.userData.orgHeight = size.y;

    // Set rotation based on face
    if (faceId === FaceID.East) {
      wrappedPart.userData.orgDepth = size.x;
      wrappedPart.rotation.y = Math.PI / 2;
    } else if (faceId === FaceID.West) {
      wrappedPart.userData.orgDepth = size.x;
      wrappedPart.rotation.y = -Math.PI / 2;
    } else if (faceId === FaceID.South0 || faceId === FaceID.South1) {
      wrappedPart.userData.orgWidth = size.x;
    } else if (faceId === FaceID.North0 || faceId === FaceID.North1) {
      wrappedPart.userData.orgWidth = size.x;
      wrappedPart.rotation.y = -Math.PI;
    }

    // Add to scene
    state.group.add(wrappedPart);

    // Update surface part list
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (surfacePart) {
      surfacePart.partType = partType;
      surfacePart.part = wrappedPart;
    }

    // Set as active part
    state.activePart = wrappedPart;

    // Update part position/scale based on dimensions
    updatePartStatus(wrappedPart);

    // Apply accessory textures
    applyAccessoryTextures(wrappedPart);
    preloadTexturesForPartType({ partType });

    // Default movement states matching Vue
    if (partType === PartType.FRAMELESS_GLASS_DOOR) {
      wrappedPart.userData.isLeft = true;
      wrappedPart.userData.sliderValue = 0;
      moveSlidingDoor4(wrappedPart, 0);
    } else if (partType === PartType.TILTING_HORIZONTAL_SCREEN_PANEL) {
      const defaultValue = 49;
      wrappedPart.userData.sliderValue = defaultValue;
      wrappedPart.userData.sliderValue2 = defaultValue;
      moveTiltingHorizontal(wrappedPart, defaultValue);
    } else if (partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL) {
      wrappedPart.userData.sliderValue = 0;
      wrappedPart.userData.sliderValue2 = 0;
      moveSlidingAdjustableShutterPanels(wrappedPart, 0);
      moveSlidingAdjustableShutterBlades(wrappedPart, 0);
    }

    resolve();
  };

  /**
   * Load both the 2-track and 3-track shutter sub-models in parallel, tag
   * their meshes for the sliding animation, and return them combined into a
   * single Group. The wrapper that contains both is what gets passed to
   * `attachLoadedPart` like any other surface part.
   *
   * At runtime `updateShutterTrackVisibility` toggles which sub-model is
   * shown based on the current face span (≥ 420 cm → 3-track, else 2-track).
   * Loading both up-front means width/depth changes never have to swap GLBs.
   */
  const loadSlidingAdjustableShutterCombined = async (): Promise<THREE.Group> => {
    const [model2, model3] = await Promise.all([
      loadSurfacePartModel({
        modelPath: getSlidingAdjustableShutterModelPath({ trackCount: 2 }),
      }),
      loadSurfacePartModel({
        modelPath: getSlidingAdjustableShutterModelPath({ trackCount: 3 }),
      }),
    ]);

    // The 3-track model is authored wider; bring it down to the same 2.791 m
    // base width as the rest of the accessories (matches factory example).
    model3.scale.x = 2.791 / 4.5;

    (model2.userData as Record<string, unknown>).trackCount = 2;
    (model3.userData as Record<string, unknown>).trackCount = 3;

    tagSlidingAdjustableShutterMeshes(model2, 2);
    tagSlidingAdjustableShutterMeshes(model3, 3);

    const combined = new THREE.Group();
    combined.add(model2);
    combined.add(model3);
    return combined;
  };

  // Add part to face
  const addPart = async (faceId: FaceIDType, partType: number): Promise<void> => {
    // Delete existing part on this face first
    deletePart(faceId);

    let partModel: THREE.Group;
    if (partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL) {
      partModel = await loadSlidingAdjustableShutterCombined();
    } else {
      const modelPath = getSurfacePartModelPath({ faceId, partType });
      if (!modelPath) {
        console.error('Unknown part type:', partType);
        return;
      }
      partModel = await loadSurfacePartModel({ modelPath });
    }

    return new Promise((resolve) => {
      attachLoadedPart({ faceId, partModel, partType, resolve });
    });
  };

  // Get/set active part
  const getActivePart = (): THREE.Group | null => state.activePart;
  const setActivePart = (part: THREE.Group | null) => { state.activePart = part; };

  // Update part status (position, scale, visibility)
  const updatePartStatus = (part: THREE.Object3D) => {
    const userData = part.userData as Record<string, unknown>;
    if (!userData.isPart) return;

    const roomCount = getRoomCount();
    const room1Width = getRoom1Width();
    const room2Width = getRoom2Width();
    const modelTotalWidth = getModelTotalWidth();
    // Per-face room reference: South0/North0 belong to room 1, South1/North1 to room 2.
    const isRoom2Face =
      (userData.faceId as FaceIDType) === FaceID.South1 ||
      (userData.faceId as FaceIDType) === FaceID.North1;
    const roomWidth = isRoom2Face && roomCount === 2 ? room2Width : room1Width;
    const room1CenterX =
      roomCount === 2 ? -modelTotalWidth / 2 + room1Width / 2 : 0;
    const room2CenterX =
      roomCount === 2 ? modelTotalWidth / 2 - room2Width / 2 : 0;
    const faceId = userData.faceId as FaceIDType;
    const partType = userData.partType as number;

    const columnDepth = 0.15 - 0.015; // = 0.135, used for wall mode
    const diffFaceWidth = 0.110 / 4;  // = 0.0275
    const diffRoomWidth = roomWidth - ORG_WIDTH;
    const diffDepth = state.depthValue - ORG_DEPTH + (state.hasWall ? columnDepth : 0);
    const diffHeight = state.heightValue - ORG_HEIGHT;

    // Door types need slight inset from frame edge
    let offsetPart = 0;
    if ([PartType.FRAMELESS_GLASS_DOOR, PartType.SLIMLINE_FRAME_GLASS_DOOR, PartType.SLIDING_DOOR_FABRIC].includes(partType as 8 | 9 | 10)) {
      offsetPart = 0.0156;
    }

    const orgHeight = userData.orgHeight as number || 2.5;
    const orgWidth = userData.orgWidth as number || 2.791;
    const orgDepth = userData.orgDepth as number || 2.791;
    const isSlidingAdjustableShutter = partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL;

    // Height scaling applies to all parts
    part.scale.y = (orgHeight + diffHeight) / orgHeight;

    if (roomCount === 1) {
      if (faceId === FaceID.South1 || faceId === FaceID.North1) {
        part.visible = false;
        return;
      }

      if (faceId === FaceID.East) {
        part.visible = true;
        part.position.x = roomWidth / 2 - offsetPart;
        part.position.z = state.hasWall ? (-columnDepth / 2) : 0;
        part.scale.x = (orgDepth + diffDepth) / orgDepth;
      } else if (faceId === FaceID.West) {
        part.visible = true;
        part.position.x = -roomWidth / 2 + offsetPart;
        part.position.z = state.hasWall ? (-columnDepth / 2) : 0;
        part.scale.x = (orgDepth + diffDepth) / orgDepth;
      } else if (faceId === FaceID.South0) {
        part.visible = true;
        part.position.x = 0;
        part.position.z = state.depthValue / 2 - offsetPart;
        part.scale.x = (orgWidth + diffRoomWidth) / orgWidth;
      } else if (faceId === FaceID.North0) {
        part.visible = !state.hasWall; // Hidden when wall mounted
        part.position.x = 0;
        part.position.z = -state.depthValue / 2 + offsetPart;
        part.scale.x = (orgWidth + diffRoomWidth) / orgWidth;
      }
    } else if (roomCount === 2) {
      if (faceId === FaceID.East) {
        part.scale.x = (orgDepth + diffDepth) / orgDepth;
        part.visible = true;
        part.position.x = modelTotalWidth / 2 - offsetPart;
        part.position.z = state.hasWall ? (-columnDepth / 2) : 0;
      } else if (faceId === FaceID.West) {
        part.scale.x = (orgDepth + diffDepth) / orgDepth;
        part.visible = true;
        part.position.x = -modelTotalWidth / 2 + offsetPart;
        part.position.z = state.hasWall ? (-columnDepth / 2) : 0;
      } else if (faceId === FaceID.South0) {
        part.scale.x = (orgWidth + diffRoomWidth + diffFaceWidth) / orgWidth;
        part.visible = true;
        part.position.x = room1CenterX + diffFaceWidth / 2;
        part.position.z = state.depthValue / 2 - offsetPart;
      } else if (faceId === FaceID.North0) {
        part.scale.x = (orgWidth + diffRoomWidth + diffFaceWidth) / orgWidth;
        part.visible = !state.hasWall; // Hidden when wall mounted
        part.position.x = room1CenterX + diffFaceWidth / 2;
        part.position.z = -state.depthValue / 2 + offsetPart;
      } else if (faceId === FaceID.South1) {
        part.scale.x = (orgWidth + diffRoomWidth + diffFaceWidth) / orgWidth;
        part.visible = true;
        part.position.x = room2CenterX - diffFaceWidth / 2;
        part.position.z = state.depthValue / 2 - offsetPart;
      } else if (faceId === FaceID.North1) {
        part.scale.x = (orgWidth + diffRoomWidth + diffFaceWidth) / orgWidth;
        part.visible = !state.hasWall; // Hidden when wall mounted
        part.position.x = room2CenterX - diffFaceWidth / 2;
        part.position.z = -state.depthValue / 2 + offsetPart;
      }
    }

    if (isSlidingAdjustableShutter) {
      // The shutter spans the full side; positioning + scaling already came
      // from the generic E/W or N/S branch above. Only thing left is to show
      // the right sub-model: 2-track when the span is < 420 cm, 3-track
      // otherwise.
      const isEastWest = faceId === FaceID.East || faceId === FaceID.West;
      const spanMeters = isEastWest ? state.depthValue : roomWidth;
      const isBigSpan =
        spanMeters * 100 >= SLIDING_ADJUSTABLE_SHUTTER_TRACK_THRESHOLD_CM;

      part.traverse((child) => {
        const tc = (child.userData as Record<string, unknown>).trackCount;
        // Only touch the sub-model wrappers (track count is also set on
        // child meshes, but their visibility is irrelevant — visibility
        // cascades from the parent).
        if (child instanceof THREE.Mesh) return;
        if (tc === 2) child.visible = !isBigSpan;
        else if (tc === 3) child.visible = isBigSpan;
      });
    }

    const isDoor6 = roomCount === 1
      ? (faceId === FaceID.South0 || faceId === FaceID.North0) && state.widthValue >= 32 && state.widthValue < 39
      : (faceId === FaceID.South0 || faceId === FaceID.South1 || faceId === FaceID.North0 || faceId === FaceID.North1) && state.widthValue >= 64;

    updatePartFabric(part, isDoor6);
    updatePartSlimlineFrameGlassDoor(part, isDoor6);
    updatePartInner(part as THREE.Group);
  };

  const wallSceneZKnots: Array<[number, number]> = [
    [3, 0.011],
    [3.15, -0.064],
    [3.3, -0.139],
    [3.45, -0.214],
    [3.6, -0.289],
    [3.75, -0.364],
    [3.9, -0.439],
    [4.0, -0.489],
  ];

  const interpolateWallSceneZ = (depth: number): number => {
    const knots = wallSceneZKnots;
    if (depth <= knots[0][0]) {
      const [d0, z0] = knots[0];
      const [d1, z1] = knots[1];
      return z0 + (depth - d0) * (z1 - z0) / (d1 - d0);
    }
    if (depth >= knots[knots.length - 1][0]) {
      const [d0, z0] = knots[knots.length - 2];
      const [d1, z1] = knots[knots.length - 1];
      return z0 + (depth - d0) * (z1 - z0) / (d1 - d0);
    }
    for (let i = 0; i < knots.length - 1; i += 1) {
      const [d0, z0] = knots[i];
      const [d1, z1] = knots[i + 1];
      if (depth >= d0 && depth <= d1) {
        const t = (depth - d0) / (d1 - d0);
        return z0 + t * (z1 - z0);
      }
    }
    return -0.489;
  };

  const updateWallScenePosition = () => {
    if (!state.currentScene) return;
    const sceneModel = state.sceneModels.get(state.currentScene);
    if (!sceneModel || sceneModel.userData?.hasWall !== true) return;

    sceneModel.position.z = interpolateWallSceneZ(state.depthValue);
  };

  const setSceneVisibility = (visible: boolean) => {
    state.sceneVisible = visible;
    const sceneModel = state.currentScene ? state.sceneModels.get(state.currentScene) : null;
    if (visible && sceneModel) {
      sceneModel.visible = true;
      if (state.skybox) state.skybox.visible = false;
      if (state.ground) state.ground.visible = false;
      if (state.base) state.base.visible = false;
    } else {
      if (sceneModel) sceneModel.visible = false;
      if (state.skybox) state.skybox.visible = true;
      if (state.ground) state.ground.visible = true;
      if (state.base) state.base.visible = true;
    }
    updateWallVisibility();
  };

  // Set scene/background
  const setScene = async (sceneId: SceneId): Promise<void> => {
    // If same scene selected, toggle off
    if (sceneId === state.currentScene) {
      // Hide scene model
      const sceneModel = state.sceneModels.get(sceneId!);
      if (sceneModel) sceneModel.visible = false;

      // Show skybox and ground
      if (state.skybox) state.skybox.visible = true;
      if (state.ground) state.ground.visible = true;
      if (state.base) state.base.visible = true;

      state.currentScene = null;
      updateWallVisibility();
      return;
    }

    // Hide all scene models first
    state.sceneModels.forEach((model) => { model.visible = false; });

    if (!sceneId) {
      // Show default skybox
      if (state.skybox) state.skybox.visible = true;
      if (state.ground) state.ground.visible = true;
      if (state.base) state.base.visible = true;
      state.currentScene = null;
      updateWallVisibility();
      return;
    }

    const sceneConfig = SCENES.find((s) => s.id === sceneId);
    if (!sceneConfig) return;

    // Check if scene is already loaded
    let sceneModel = state.sceneModels.get(sceneId);

    if (!sceneModel) {
      // Load scene model
      const loader = new GLTFLoader();
      try {
        const gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) => {
          loader.load(sceneConfig.glb, resolve, undefined, reject);
        });

        sceneModel = gltf.scene;
        sceneModel.updateMatrixWorld(true);
        sceneModel.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
          // Hide positioning points
          if (getNodeName(child) === '1号定位点' || getNodeName(child) === '0-捕捉点') {
            child.visible = false;
          }
        });
        // Reduce z-fighting on large flat floors in scene models
        const floorAdjustVersion = 2;
        const previousAdjustVersion = sceneModel.userData.floorAdjustedVersion ?? (sceneModel.userData.floorAdjusted ? 1 : 0);
        if (previousAdjustVersion !== floorAdjustVersion) {
          const tmpSize = new THREE.Vector3();
          const worldBox = new THREE.Box3();
          const applyFloorOffset = (mesh: THREE.Mesh, priority: number, materials: THREE.Material[]) => {
            const renderOrder = 100 + priority;
            const offsetFactor = -priority * 2.5;
            if (mesh.userData.floorBaseY === undefined) {
              const baseY = previousAdjustVersion === 1 ? mesh.position.y - priority * 0.002 : mesh.position.y;
              mesh.userData.floorBaseY = baseY;
            }
            mesh.position.y = mesh.userData.floorBaseY as number;
            mesh.renderOrder = renderOrder;
            materials.forEach((mat) => {
              const material = mat as THREE.MeshStandardMaterial;
              material.polygonOffset = true;
              material.polygonOffsetFactor = offsetFactor;
              material.polygonOffsetUnits = offsetFactor;
              const isTransparent = material.transparent || material.opacity < 0.99;
              material.depthWrite = !isTransparent;
              material.depthTest = true;
              material.needsUpdate = true;
            });
          };
          const getFloorPriority = (scene: SceneId, matName: string): number | null => {
            if (scene === 'courtyard') {
              const priorityMap: Record<string, number> = {
                '草地': 0,
                '水': 0,
                'PD-1': 1,
                '石材面': 1,
                '鹅卵石_地面': 2,
                'RENXINGDAO': 3,
                'MULU': 3,
                'PD-12': 3,
                '木地板': 4,
              };
              return priorityMap[matName] ?? null;
            }
            if (scene === 'wall') {
              const priorityMap: Record<string, number> = {
                'CD': 0,
                '鹅卵石': 1,
                '灰色墙面': 2,
                '楼板': 3,
                '木地板': 4,
              };
              return priorityMap[matName] ?? null;
            }
            return null;
          };
          sceneModel.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) return;
            worldBox.setFromObject(child);
            worldBox.getSize(tmpSize);
            const maxXZ = Math.max(tmpSize.x, tmpSize.z);
            const isFlat = tmpSize.y < 0.08 && maxXZ > 1;
            if (!isFlat) return;
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            let priority: number | null = null;
            materials.forEach((mat) => {
              const material = mat as THREE.MeshStandardMaterial;
              const matName = material?.name || '';
              const candidate = getFloorPriority(sceneId, matName);
              if (candidate === null) return;
              priority = priority === null ? candidate : Math.max(priority, candidate);
            });
            if (priority === null) return;
            applyFloorOffset(child, priority, materials);
          });
          sceneModel.userData.floorAdjusted = true;
          sceneModel.userData.floorAdjustedVersion = floorAdjustVersion;
        }
        sceneModel.userData.hasWall = sceneConfig.hasWall;

        state.sceneModels.set(sceneId, sceneModel);
        state.group.parent?.add(sceneModel);
      } catch (error) {
        console.error('Error loading scene:', error);
        return;
      }
    }

    // Show scene model, hide skybox and ground
    sceneModel.visible = state.sceneVisible;
    if (state.skybox) state.skybox.visible = !state.sceneVisible;
    if (state.ground) state.ground.visible = !state.sceneVisible;
    if (state.base) state.base.visible = !state.sceneVisible;

    state.currentScene = sceneId;

    // Auto-toggle wall mounted based on scene
    if (sceneConfig.hasWall !== state.hasWall) {
      setWallMounted(sceneConfig.hasWall);
    }

    updateWallVisibility();
    updateWallScenePosition();
  };

  // Set wall mounted mode
  const setWallMounted = (value: boolean) => {
    state.hasWall = value;
    const wallPatchScale = 3.84;

    // Update North face column visibility
    state.group.traverse((child) => {
      const userData = child.userData as Record<string, unknown>;
      const tag = userData.tag as string || '';
      const dir = userData.dir as string || '';

      // Hide/show North-facing columns when wall mounted
      if (/立柱/.test(tag) && (dir === '北' || dir === '东北' || dir === '西北')) {
        child.visible = !value;
      }
      if (tag === '横梁北小块') {
        child.scale.z = value ? wallPatchScale : 1;
      }
    });

    // Update all part positions
    state.group.traverse((child) => {
      updatePartStatus(child);
    });

    updateColumnSurfacePartVisibility();
    updateWallVisibility();
  };

  // Toggle LED visibility
  const toggleLedVisibility = () => {
    state.ledsVisible = !state.ledsVisible;
    applyLedEmission({
      intensity: state.ledsVisible ? state.ledEmissiveIntensity : 0,
    });
    // Swap normal ↔ LED louvers so the upgrade visually changes the model
    updateBaiyeVisibility();
  };

  const getLedsVisible = (): boolean => {
    return state.ledsVisible;
  };

  const setLedEmissiveIntensity = (intensity: number) => {
    const safeIntensity = Number.isFinite(intensity) ? Math.max(0, intensity) : DEFAULT_LED_EMISSIVE_INTENSITY;
    state.ledEmissiveIntensity = safeIntensity;

    if (!state.ledsVisible) {
      return;
    }

    applyLedEmission({ intensity: state.ledEmissiveIntensity });
  };

  // Create add icons on face surfaces
  const createAddIcons = async (): Promise<void> => {
    const requestId = ++addIconRequestId;
    // Clear existing icons
    state.addIcons.forEach((icon) => {
      state.group.remove(icon);
      icon.geometry.dispose();
      (icon.material as THREE.Material).dispose();
    });
    state.addIcons.clear();

    const texture = await loadAddIconTexture();
    if (requestId !== addIconRequestId) return;

    // Create icon for each surface
    for (let i = 0; i < state.preSetsurface.length; i++) {
      const surface = state.preSetsurface[i];
      const surfacePart = state.surfacePartList[i];

      if (!surfacePart || !surfacePart.visible) continue;

      // Create icon geometry and material
      const geometry = new THREE.PlaneGeometry(0.17, 0.17);
      const material = new THREE.MeshBasicMaterial({
        map: texture.clone(),
        transparent: true,
        side: THREE.DoubleSide,
        depthTest: true,
      });

      const icon = new THREE.Mesh(geometry, material);

      // Get surface bounding box center
      const box = new THREE.Box3().setFromObject(surface);
      const center = new THREE.Vector3();
      box.getCenter(center);

      icon.position.copy(center);
      icon.position.y = 0.15; // Position near bottom

      const userData = surface.userData as Record<string, unknown>;
      const tag = userData.tag as string || '';
      const faceId = surfacePart.faceId;

      // Adjust position and rotation based on face direction
      if (/南面/.test(tag)) {
        icon.position.z = icon.position.z > 0 ? icon.position.z + 0.02 : icon.position.z - 0.02;
        icon.rotation.y = 0;
      } else if (/北面/.test(tag)) {
        icon.position.z = icon.position.z > 0 ? icon.position.z + 0.02 : icon.position.z - 0.02;
        icon.rotation.y = Math.PI;
      } else if (/东面/.test(tag)) {
        icon.position.x = icon.position.x > 0 ? icon.position.x + 0.02 : icon.position.x - 0.02;
        icon.rotation.y = Math.PI / 2;
      } else if (/西面/.test(tag)) {
        icon.position.x = icon.position.x > 0 ? icon.position.x + 0.02 : icon.position.x - 0.02;
        icon.rotation.y = -Math.PI / 2;
      }

      // Store face ID in icon userData for click detection
      icon.userData.faceId = faceId;
      icon.userData.isAddIcon = true;

      state.group.add(icon);
      state.addIcons.set(faceId, icon);
    }

    updateAddIconVisibility();
    void createRemoveIcons();
  };

  // Get all add icons for raycasting
  const getAddIcons = (): THREE.Mesh[] => {
    return Array.from(state.addIcons.values());
  };

  // Update add icon visibility based on whether face has an accessory
  const updateAddIconVisibility = (): void => {
    state.addIcons.forEach((icon, faceId) => {
      const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
      // Hide icon if face has accessory or is not visible
      icon.visible = state.addIconsVisible && surfacePart?.visible === true && surfacePart?.partType === null;
    });
    updateRemoveIconVisibility();
  };

  const setAddIconsVisible = (visible: boolean): void => {
    state.addIconsVisible = visible;
    updateAddIconVisibility();
  };

  // Create remove icons on face surfaces (mirrors createAddIcons)
  const createRemoveIcons = async (): Promise<void> => {
    const requestId = ++removeIconRequestId;
    state.removeIcons.forEach((icon) => {
      state.group.remove(icon);
      icon.geometry.dispose();
      (icon.material as THREE.Material).dispose();
    });
    state.removeIcons.clear();

    const texture = await loadRemoveIconTexture();
    if (requestId !== removeIconRequestId) return;

    for (let i = 0; i < state.preSetsurface.length; i++) {
      const surface = state.preSetsurface[i];
      const surfacePart = state.surfacePartList[i];
      if (!surfacePart || !surfacePart.visible) continue;

      const geometry = new THREE.PlaneGeometry(0.17, 0.17);
      const material = new THREE.MeshBasicMaterial({
        map: texture.clone(),
        transparent: true,
        alphaTest: 0.1,
        side: THREE.DoubleSide,
        depthTest: true,
      });
      const icon = new THREE.Mesh(geometry, material);

      const box = new THREE.Box3().setFromObject(surface);
      const center = new THREE.Vector3();
      box.getCenter(center);
      icon.position.copy(center);
      icon.position.y = 0.15;

      const userData = surface.userData as Record<string, unknown>;
      const tag = userData.tag as string || '';
      const faceId = surfacePart.faceId;

      if (/南面/.test(tag)) {
        icon.position.z = icon.position.z > 0 ? icon.position.z + 0.02 : icon.position.z - 0.02;
        icon.rotation.y = 0;
      } else if (/北面/.test(tag)) {
        icon.position.z = icon.position.z > 0 ? icon.position.z + 0.02 : icon.position.z - 0.02;
        icon.rotation.y = Math.PI;
      } else if (/东面/.test(tag)) {
        icon.position.x = icon.position.x > 0 ? icon.position.x + 0.02 : icon.position.x - 0.02;
        icon.rotation.y = Math.PI / 2;
      } else if (/西面/.test(tag)) {
        icon.position.x = icon.position.x > 0 ? icon.position.x + 0.02 : icon.position.x - 0.02;
        icon.rotation.y = -Math.PI / 2;
      }

      icon.userData.faceId = faceId;
      icon.userData.isRemoveIcon = true;
      icon.visible = false;

      state.group.add(icon);
      state.removeIcons.set(faceId, icon);
    }

    updateRemoveIconVisibility();
  };

  // Update remove icon visibility: show on faces WITH an accessory, always
  const updateRemoveIconVisibility = (): void => {
    state.removeIcons.forEach((icon, faceId) => {
      const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
      icon.visible = surfacePart?.visible === true && surfacePart?.partType !== null;
    });
  };

  const getRemoveIcons = (): THREE.Mesh[] => {
    return Array.from(state.removeIcons.values());
  };

  // Set accessory left/right position (for glass doors)
  const setAccessoryLeftRight = (faceId: FaceIDType, isLeft: boolean) => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return;

    const part = surfacePart.part;
    part.userData.isLeft = isLeft;

    if (part.userData.partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL) {
      updatePartStatus(part);
      return;
    }

    // Rotate the inner group
    if (part.children[0]) {
      part.children[0].rotation.y = isLeft ? 0 : Math.PI;
    }

    if (part.userData.partType === PartType.FRAMELESS_GLASS_DOOR) {
      const currentValue = part.userData.sliderValue as number | undefined;
      moveSlidingDoor4(part, currentValue ?? 0);
    }
  };

  const moveSlidingFabric = (part: THREE.Group, value = 0) => {
    const percent = value / 100;
    part.traverse((child: any) => {
      if (getNodeName(child) === 'Rectangle008') {
        const min = -0.368;
        const max = -1.025;
        child.position.x = min + (max - min) * percent;
      }
      if (getNodeName(child) === 'Rectangle009') {
        const min = -0.368;
        const max = -1.025;
        child.position.x = min + (max - min) * percent;
      }
      if (getNodeName(child) === 'Rectangle011') {
        const min = 0.367;
        const max = 1.025;
        child.position.x = min + (max - min) * percent;
      }
      if (getNodeName(child) === 'Rectangle012') {
        const min = 0.367;
        const max = 1.025;
        child.position.x = min + (max - min) * percent;
      }
    });

    part.traverse((child: any) => {
      if (child.userData.door === 1) {
        const min = 0;
        const max = -0.935 * 2;
        child.position.x = min + (max - min) * Math.max(percent - 0.5, 0);
      } else if (child.userData.door === 2) {
        const min = 0;
        const max = -0.935 * 2;
        child.position.x = min + (max - min) * percent;
      } else if (child.userData.door === 3) {
        const min = 0;
        const max = 0.935 * 2;
        child.position.x = min + (max - min) * percent;
      } else if (child.userData.door === 4) {
        const min = 0;
        const max = 0.935 * 2;
        child.position.x = min + (max - min) * Math.max(percent - 0.5, 0);
      }
    });
  };

  const moveWindScreen = (part: THREE.Group, value = 0) => {
    const percent = Math.min(value, 95) / 100;
    const hengliangHeight = state.hengliangSize?.y ?? 0.11;
    const faceHeight = 2.6 - hengliangHeight;
    const origY = 1.16049874;
    let positionY = 0;

    part.traverse((child: any) => {
      if (getNodeName(child) === 'Rectangle2059403192') {
        const height = faceHeight - 0.1 - 0.05;
        const mesh = child as THREE.Mesh;
        mesh.scale.z = (1 - percent) * height / (origY * 2);
        mesh.position.y = height / 2 + height * percent / 2;
        updateUV4Curtain(mesh, 1 - percent);
        positionY = mesh.position.y - height * (1 - percent) / 2 + 0.025;
      }
    });

    part.traverse((child: any) => {
      if (getNodeName(child) === '对象037') {
        child.position.y = positionY;
      }
    });
  };

  const moveSlidingDoor4 = (part: THREE.Group, value = 0) => {
    const oldValue = (part.userData.oldValue as number | undefined) ?? 0;
    part.userData.sliderValue = value;

    const percent = 1 - value / 100;
    const min = -1.047;
    const max = 1.022;
    const minHand = -0.753;
    const minDizuo = -1.047;

    const doors: THREE.Mesh[] = [];
    const hands: THREE.Mesh[] = [];
    const dizuo: THREE.Mesh[] = [];
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403223' && doors.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403221' && doors.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403219' && doors.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403217' && doors.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403224' && hands.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403222' && hands.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403220' && hands.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403218' && hands.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403216' && dizuo.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403225' && dizuo.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403226' && dizuo.push(child));
    part.traverse((child: any) => getNodeName(child) === 'Rectangle2059403227' && dizuo.push(child));

    const isLeft = part.userData.isLeft !== false;
    if (isLeft) {
      doors[0].position.z = 0.033;
      doors[1].position.z = 0.011;
      doors[2].position.z = -0.011;
      doors[3].position.z = -0.033;
      hands[0].position.z = 0.033;
      hands[1].position.z = 0.011;
      hands[2].position.z = -0.011;
      hands[3].position.z = -0.033;
      dizuo[0].position.z = 0.033;
      dizuo[1].position.z = 0.011;
      dizuo[2].position.z = -0.011;
      dizuo[3].position.z = -0.033;
    } else {
      doors[3].position.z = 0.033;
      doors[2].position.z = 0.011;
      doors[1].position.z = -0.011;
      doors[0].position.z = -0.033;
      hands[3].position.z = 0.033;
      hands[2].position.z = 0.011;
      hands[1].position.z = -0.011;
      hands[0].position.z = -0.033;
      dizuo[3].position.z = 0.033;
      dizuo[2].position.z = 0.011;
      dizuo[1].position.z = -0.011;
      dizuo[0].position.z = -0.033;
    }

    doors[0].position.x = min + (max - min) * percent;

    if (doors[1].position.x < doors[0].position.x - (max - min) / 3) {
      doors[1].position.x = doors[0].position.x - (max - min) / 3;
    }
    if (doors[2].position.x < doors[1].position.x - (max - min) / 3) {
      doors[2].position.x = doors[1].position.x - (max - min) / 3;
    }

    if (percent > 1 / 3 && value < oldValue && (doors[0].position.x - doors[1].position.x) > (max - min) / 3) {
      doors[1].position.x = doors[0].position.x - (max - min) / 3;
    }
    if (percent > 2 / 3 && value < oldValue && (doors[1].position.x - doors[2].position.x) > (max - min) / 3) {
      doors[2].position.x = doors[1].position.x - (max - min) / 3;
    }

    if (doors[1].position.x > doors[0].position.x) {
      doors[1].position.x = doors[0].position.x;
    }
    if (doors[2].position.x > doors[0].position.x) {
      doors[2].position.x = doors[0].position.x;
    }

    for (let i = 0; i < hands.length; i++) {
      hands[i].position.x = minHand + doors[i].position.x - min;
    }
    for (let i = 0; i < dizuo.length; i++) {
      dizuo[i].position.x = minDizuo + doors[i].position.x - min;
    }

    part.userData.oldValue = value;
  };

  const moveSlidingDoor2 = (part: THREE.Group, value = 0) => {
    const percent = value / 100;

    part.traverse((child: any) => {
      if (getNodeName(child) === 'Rectangle015') {
        const min = -0.357;
        const max = -1.05;
        child.position.x = min + (max - min) * percent;
      }
      if (getNodeName(child) === 'Rectangle016') {
        const min = -0.357;
        const max = -1.05;
        child.position.x = min + (max - min) * percent;
      }
      if (getNodeName(child) === '网格244') {
        const orgX = -0.039;
        const min = -0.357;
        const max = -1.05;
        child.position.x = orgX + (max - min) * percent;
      }
      if (getNodeName(child) === 'Rectangle018') {
        const min = 0.353;
        const max = 1.05;
        child.position.x = min + (max - min) * percent;
      }
      if (getNodeName(child) === 'Rectangle019') {
        const orgX = 0.356;
        const min = 0.353;
        const max = 1.05;
        child.position.x = orgX + (max - min) * percent;
      }
      if (getNodeName(child) === '网格245') {
        const orgX = 0.039;
        const min = 0.353;
        const max = 1.05;
        child.position.x = orgX + (max - min) * percent;
      }
    });

    part.traverse((child: any) => {
      if (child.userData.door === 1) {
        const min = 0;
        const max = -0.931 * 2;
        child.position.x = min + (max - min) * Math.max(percent - 0.5, 0);
      }
      if (child.userData.door === 2) {
        const min = 0;
        const max = -1.85;
        child.position.x = min + (max - min) * percent;
      }
      if (child.userData.door === 3) {
        const min = 0;
        const max = 1.85;
        child.position.x = min + (max - min) * percent;
      }
      if (child.userData.door === 4) {
        const min = 0;
        const max = 0.931 * 2;
        child.position.x = min + (max - min) * Math.max(percent - 0.5, 0);
      }
    });

    part.userData.oldValue = value;
  };

  const moveTiltingHorizontal = (part: THREE.Group, value = 0) => {
    const names1 = ['Line1244', 'Line1226', 'Line1229', 'Line1230', 'Line1231', 'Line1232', 'Line1233', 'Line1234', 'Line1235', 'Line1236', 'Line1237', 'Line1238', 'Line1239', 'Line1240', 'Line1241', 'Line1242', 'Line1243'];
    const names2 = ['Line1225', 'Line1207', 'Line1210', 'Line1211', 'Line1212', 'Line1213', 'Line1214', 'Line1215', 'Line1216', 'Line1217', 'Line1218', 'Line1219', 'Line1220', 'Line1221', 'Line1222', 'Line1223', 'Line1224'];
    const names = [...names1, ...names2];
    part.traverse((child: any) => {
      for (let i = 0; i < names.length; i++) {
        if ((child.userData?.name || '').startsWith(names[i])) {
          const mesh = child as THREE.Mesh;
          const org = -180 * Math.PI / 180;
          mesh.rotation.x = org * (1 + value / 180);
          break;
        }
      }
    });
  };

  /**
   * Primary "Open / Close" animation for the sliding adjustable shutter:
   * translate each door panel along its local X based on `userData.door` and
   * `userData.minX`. Both 2-track and 3-track sub-models animate from the
   * same slider value — only one is actually visible at a time.
   *
   * Travel distances mirror the factory configurator (see
   * `moveLouverSliding` in ad3dvivew-front/.../moveparts.service.tsx).
   */
  const moveSlidingAdjustableShutterPanels = (part: THREE.Group, value = 0) => {
    // 0..100 → 0..0.97 (keeps panels from over-running past the end stops)
    const percent = (value / 100) * 0.97;

    part.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const userData = child.userData as Record<string, unknown>;

      const doorId = userData.door as number | undefined;
      const minX = userData.minX as number | undefined;
      const meshTrackCount = userData.trackCount as number | undefined;
      if (doorId === undefined || minX === undefined || meshTrackCount === undefined) return;

      let displacement = 0;
      if (meshTrackCount === 2) {
        // 2-track: door 1 slides left, door 2 slides right.
        const travel = doorId === 1 ? (-1.05 - (-0.357)) : (1.05 - 0.353);
        displacement = travel * percent;
      } else if (meshTrackCount === 3) {
        // 3-track: outer doors travel half as far as the inner doors so the
        // four panels nest neatly when open.
        if (doorId === 1) displacement = -0.75 * percent;
        else if (doorId === 2) displacement = -1.5 * percent;
        else if (doorId === 3) displacement = 1.5 * percent;
        else if (doorId === 4) displacement = 0.75 * percent;
      }

      child.position.x = minX + displacement;
    });
  };

  /**
   * Secondary "Tilt" animation: rotate each louver blade around its local X
   * axis. Same formula the existing tilting-horizontal screen uses, scoped
   * to meshes flagged `isShutterBlade` by the tagger.
   */
  const moveSlidingAdjustableShutterBlades = (part: THREE.Group, value = 0) => {
    const angle = ((100 - value) / 100) * (Math.PI / 6);

    part.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const userData = child.userData as Record<string, unknown>;
      if (userData.isShutterBlade !== true) return;

      if (typeof userData.shutterOriginalRotationX !== 'number') {
        userData.shutterOriginalRotationX = child.rotation.x;
      }
      child.rotation.x = (userData.shutterOriginalRotationX as number) + angle;
    });
  };

  // Move accessory (for curtains, doors - animates open/close)
  const moveAccessory = (faceId: FaceIDType, value: number) => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return;

    const part = surfacePart.part;
    part.userData.sliderValue = value;

    const partType = part.userData.partType as number;
    if (partType === PartType.MANUAL_WIND_SCREEN || partType === PartType.MOTORIZED_WIND_SCREEN) {
      moveWindScreen(part, value);
    } else if (partType === PartType.SLIDING_DOOR_FABRIC) {
      moveSlidingFabric(part, value);
    } else if (partType === PartType.TILTING_HORIZONTAL_SCREEN_PANEL) {
      moveTiltingHorizontal(part, value);
    } else if (partType === PartType.FRAMELESS_GLASS_DOOR) {
      moveSlidingDoor4(part, value);
    } else if (partType === PartType.SLIMLINE_FRAME_GLASS_DOOR) {
      moveSlidingDoor2(part, value);
    } else if (partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL) {
      moveSlidingAdjustableShutterPanels(part, value);
    }
  };

  const moveAccessorySecondary = (faceId: FaceIDType, value: number) => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return;

    const part = surfacePart.part;
    part.userData.sliderValue2 = value;

    const partType = part.userData.partType as number;
    if (partType === PartType.TILTING_HORIZONTAL_SCREEN_PANEL) {
      moveTiltingHorizontal(part, value);
    } else if (partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL) {
      moveSlidingAdjustableShutterBlades(part, value);
    }
  };

  const getAccessoryMovement = (faceId: FaceIDType): number => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return 0;
    return (surfacePart.part.userData.sliderValue as number | undefined) ?? 0;
  };

  const getAccessorySecondaryMovement = (faceId: FaceIDType): number => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return 0;
    return (surfacePart.part.userData.sliderValue2 as number | undefined) ?? 0;
  };

  const getAccessoryLeftRight = (faceId: FaceIDType): boolean => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return true;
    return surfacePart.part.userData.isLeft !== false;
  };

  const getAccessoryTexture = (faceId: FaceIDType): string => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return '';
    const data = surfacePart.part.userData.data as Record<string, unknown> | undefined;
    return (data?.texture as string | undefined) ?? '';
  };

  const getAccessoryTextureSecondary = (faceId: FaceIDType): string => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return '';
    const data = surfacePart.part.userData.data as Record<string, unknown> | undefined;
    return (data?.texture2 as string | undefined) ?? '';
  };

  // Update merge door visibility (for screen panels with 3-6 toggleable panels)
  const updateMergeDoorsStatus = (faceId: FaceIDType, doorIndex: number, visible: boolean) => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return;

    const part = surfacePart.part;
    const doors = part.userData.doors as boolean[];
    if (!doors || doorIndex < 0 || doorIndex >= doors.length) return;

    doors[doorIndex] = visible;

    // Update visibility of the corresponding panel in the group
    const groupInner = part.children[0] as THREE.Group;
    if (groupInner && groupInner.children[doorIndex]) {
      groupInner.children[doorIndex].visible = visible;
    }

    updatePartInner(part);
  };

  // Get merge door states for a face
  const getMergeDoors = (faceId: FaceIDType): boolean[] => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return [];

    const doors = surfacePart.part.userData.doors as boolean[];
    const mergeCount = surfacePart.part.userData.mergeDoors as number || 3;

    // Return only the active doors (up to mergeCount)
    return doors ? doors.slice(0, mergeCount) : [];
  };

  // Get merge door count for a face
  const getMergeDoorCount = (faceId: FaceIDType): number => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return 0;
    return surfacePart.part.userData.mergeDoors as number || 0;
  };


  // Set accessory texture/color
  const setAccessoryTexture = (faceId: FaceIDType, textureImg: string) => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return;

    const part = surfacePart.part;
    part.userData.data = part.userData.data || {};
    part.userData.data.texture = textureImg;
    applyAccessoryTextures(part);
  };

  const setAccessoryTextureSecondary = (faceId: FaceIDType, textureImg: string) => {
    const surfacePart = state.surfacePartList.find(p => p.faceId === faceId);
    if (!surfacePart?.part) return;

    const part = surfacePart.part;
    part.userData.data = part.userData.data || {};
    part.userData.data.texture2 = textureImg;
    applyAccessoryTextures(part);
  };

  // Get accessory capabilities for customization modal
  const getAccessoryCapabilities = (partType: number): AccessoryCapabilities => {
    const canMoveTypes = [PartType.MANUAL_WIND_SCREEN, PartType.MOTORIZED_WIND_SCREEN, PartType.FRAMELESS_GLASS_DOOR, PartType.SLIMLINE_FRAME_GLASS_DOOR, PartType.SLIDING_DOOR_FABRIC, PartType.TILTING_HORIZONTAL_SCREEN_PANEL, PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL];
    const glassDoorTypes = [PartType.FRAMELESS_GLASS_DOOR, PartType.SLIMLINE_FRAME_GLASS_DOOR];
    const likeGlassDoorTypes = [PartType.FRAMELESS_GLASS_DOOR, PartType.SLIMLINE_FRAME_GLASS_DOOR, PartType.SLIDING_DOOR_FABRIC];
    const curtainTypes = [PartType.MANUAL_WIND_SCREEN, PartType.MOTORIZED_WIND_SCREEN];
    const mergeTypes = [PartType.HORIZONTAL_SCREEN_PANEL, PartType.VERTICAL_SCREEN_PANEL, PartType.DECO_SCREEN, PartType.DECO_A, PartType.DECO_YUGUWEN, PartType.WPC_FENCING_SCREEN, PartType.TILTING_HORIZONTAL_SCREEN_PANEL];
    const frameTypes = [
      PartType.HORIZONTAL_SCREEN_PANEL,
      PartType.VERTICAL_SCREEN_PANEL,
      PartType.DECO_SCREEN,
      PartType.DECO_A,
      PartType.DECO_YUGUWEN,
      PartType.WPC_FENCING_SCREEN,
      PartType.TILTING_HORIZONTAL_SCREEN_PANEL,
      PartType.WPC_WALL,
      PartType.VERTICAL_WPC_DECORATION_SLAT,
      PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL,
      PartType.MANUAL_WIND_SCREEN,
      PartType.MOTORIZED_WIND_SCREEN,
    ];

    return {
      canMove: canMoveTypes.some(t => t === partType),
      canMoveSecondary:
        partType === PartType.TILTING_HORIZONTAL_SCREEN_PANEL ||
        partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL,
      isGlassDoor: glassDoorTypes.some(t => t === partType),
      isLikeGlassDoor: likeGlassDoorTypes.some(t => t === partType),
      hasLeftRight: partType === PartType.FRAMELESS_GLASS_DOOR,
      isCurtain: curtainTypes.some(t => t === partType),
      isWpcWall: partType === PartType.WPC_WALL,
      isMergeType: mergeTypes.some(t => t === partType),
      hasFrameTexture: frameTypes.some(t => t === partType),
    };
  };

  // Dispose
  const dispose = () => {
    if (state.scene) {
      state.scene.remove(state.group);
    }
    // Clean up add icons
    state.addIcons.forEach((icon) => {
      icon.geometry.dispose();
      (icon.material as THREE.Material).dispose();
    });
    state.addIcons.clear();
    // Clean up remove icons
    state.removeIcons.forEach((icon) => {
      icon.geometry.dispose();
      (icon.material as THREE.Material).dispose();
    });
    state.removeIcons.clear();
    state.model = null;
    state.louverGroup1 = null;
    state.louverGroup2 = null;
    state.preSetsurface = [];
    state.surfacePartList = [];
    if (state.wallMesh) {
      state.wallMesh.geometry.dispose();
      (state.wallMesh.material as THREE.Material).dispose();
      state.wallMesh = null;
    }
    if (state.base) {
      state.scene?.remove(state.base);
      state.base.geometry.dispose();
      (state.base.material as THREE.Material).dispose();
      state.base = null;
    }

    configuredTextures.forEach((texture) => texture.dispose());
    configuredTextures.clear();
    configuredTexturePromises.clear();
  };

  // Set skybox and ground references for scene switching
  const setSkyboxAndGround = (skybox: THREE.Mesh | null, ground: THREE.Mesh, base?: THREE.Mesh) => {
    console.log('setSkyboxAndGround', skybox, ground, base);
    state.skybox = skybox;
    state.ground = ground;
    if (base) state.base = base;

    const sceneModel = state.currentScene ? state.sceneModels.get(state.currentScene) : null;
    const sceneVisible = state.sceneVisible && sceneModel?.visible === true;
    if (sceneVisible) {
      if (state.skybox) state.skybox.visible = false;
      if (state.ground) state.ground.visible = false;
      if (state.base) state.base.visible = false;
    } else {
      if (state.skybox) state.skybox.visible = true;
      if (state.ground) state.ground.visible = true;
      if (state.base) state.base.visible = true;
    }
  };

  return {
    init,
    loadModel,
    setSkyboxAndGround,
    setWidth,
    setDepth,
    setHeight,
    getRoomCount,
    getRoomWidth,
    getWidthValue: () => state.widthValue,
    getDepthValue: () => state.depthValue,
    getHeightValue: () => state.heightValue,
    getModelSize: () => state.modelSize.clone(),
    getModelCenter: () => state.modelCenter.clone(),
    getGroup: () => state.group,
    getSurfacePartList: () => state.surfacePartList,
    getClickableSurfaces: () => state.preSetsurface,
    getHasWall: () => state.hasWall,
    getCurrentScene: () => state.currentScene,
    setBlindRotation,
    applyFrameColor,
    applyBlindColor,
    setWallMounted,
    toggleLedVisibility,
    getLedsVisible,
    setLedEmissiveIntensity,
    setScene,
    setSceneVisibility,
    canAddPart,
    addPart,
    deletePart,
    deleteAllParts,
    updatePartStatus,
    getActivePart,
    setActivePart,
    setAccessoryLeftRight,
    moveAccessory,
    moveAccessorySecondary,
    getAccessoryMovement,
    getAccessorySecondaryMovement,
    getAccessoryLeftRight,
    getAccessoryTexture,
    getAccessoryTextureSecondary,
    getAccessoryCapabilities,
    updateMergeDoorsStatus,
    getMergeDoors,
    getMergeDoorCount,
    setAccessoryTexture,
    setAccessoryTextureSecondary,
    createAddIcons,
    getAddIcons,
    getRemoveIcons,
    updateAddIconVisibility,
    setAddIconsVisible,
    dispose,
  };
};
